import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Folder, File, MoreVertical, Download, Trash2,
  Edit3, Move, Grid, List, Search, Home, ChevronRight,
  Image, FileText, Film, Music, Archive, X, FolderPlus, FolderOpen,
  ChevronLeft, Eye, Copy, FolderInput, ArrowLeft, Activity, BarChart3, Droplets, Tag, Bell, FileInput,
  BookOpen, BookX, Star, StarOff, Users, Mail, Shield, UserPlus,
  FileCode2, FileJson2, FileSpreadsheet, FileImage, FileVideo, FileAudio,
  FileArchive, FileType, Database, Code2, Terminal, Package,
  Presentation, FileCheck, FileLock, FileKey, FileSearch, FileX,
  ChevronUp, ChevronDown, Maximize2, Minimize2, Menu, Filter, Clock, RefreshCw, Loader2,
  PanelLeft, PanelLeftClose, Settings, GripVertical
} from 'lucide-react';
import { 
  FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint,
  FaFileImage, FaFileVideo, FaFileAudio, FaFileArchive,
  FaFileAlt, FaFileCode, FaFile, FaFileCsv, FaFileContract,
  FaFileInvoice, FaFileSignature, FaFileMedical, FaFileDownload,
  FaFileUpload, FaFileImport, FaFileExport
} from 'react-icons/fa';
import { 
  SiJavascript, SiTypescript, SiPython, SiPhp, SiRuby,
  SiGo, SiRust, SiCplusplus, SiC,
  SiHtml5, SiCss3, SiReact, SiVuedotjs, SiAngular,
  SiNodedotjs, SiGit, SiDocker, SiKubernetes
} from 'react-icons/si';
import CommonLoader, { TableLoader } from '../components/CommonLoader';
import { CreateButton, DeleteButton, UpdateButton, CancelButton, ViewButton, SecurityButton } from '../components/CommonButtons';
import ResponsiveModal from '../components/ResponsiveModal';
import toast from 'react-hot-toast';
import fileService from '../services/fileService';
import dataroomService from '../services/dataroomService';
import tagService from '../services/tagService';
import tablePreferencesService from '../services/tablePreferencesService';
import PDFViewer from '../components/PDFViewer';
import FileAccessLogs from '../components/FileAccessLogs';
import FileStatistics from '../components/FileStatistics';
import WatermarkSettings from '../components/WatermarkSettings';
import StorageIndicator from '../components/StorageIndicator';
import { useAuth } from '../contexts/AuthContext';
import { needsWatermark } from '../utils/documentWatermark';
import { isPDFFile } from '../utils/fileTypeChecks';
import watermarkService from '../services/watermarkService';
import pdfWatermarkService from '../services/pdfWatermarkService';
import PDFViewerWithOverlay from '../components/PDFViewerWithOverlay';
import WatermarkedPDFPreview from '../components/WatermarkedPDFPreview';
import configService from '../services/configService';
import exportService from '../services/exportService';
import recycleBinService from '../services/recycleBinService';
// import AdvancedPDFViewer from '../components/AdvancedPDFViewer'; // Commented for now - can be enabled later
import '../styles/filePreview.scss';
import ImportTemplateModal from '../components/ImportTemplateModal';
import EditDataroomForm from '../components/EditDataroomForm';
import UploadModal from '../components/UploadModal';
import UploadTracker from '../components/UploadTracker';
import { useUpload } from '../context/UploadContext';

// Import dataroom management components
import DataroomTags from '../components/DataroomTags';
import DataroomMembers from '../components/DataroomMembers';
import DataroomRoles from '../components/DataroomRoles';
import Invitations from './Invitations';
import EmailNotificationPrefs from '../components/EmailNotificationPrefs';
import dataroomTemplateService from '../services/dataroomTemplateService';
import FolderTreeSidebar from '../components/FolderTreeSidebar';
import DataroomSidebar from '../components/DataroomSidebar';

// Helper function to format file size in MB
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  return mb.toFixed(2) + ' MB';
};

// Helper function to format bytes to appropriate unit
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileManager = ({ isRecycleBin = false, specialFilterProp = null }) => {
  const { user } = useAuth();
  const { uploadJobs, getCounts } = useUpload();
  const { dataroomUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we're in recycle bin mode based on route or prop
  const inRecycleBinMode = isRecycleBin || location.pathname.includes('/recycle-bin');

  // Determine special filter based on URL
  const getSpecialFilterFromUrl = () => {
    if (location.pathname.includes('/recycle-bin')) return 'recycle-bin';
    if (location.pathname.includes('/favorites')) return 'favorites';
    if (location.pathname.includes('/recent-uploaded')) return 'recent_uploaded';
    if (location.pathname.includes('/recent-viewed')) return 'recent_opened';
    return specialFilterProp || 'none';
  };
  
  // Get active tab from URL path or query param
  const getActiveTab = () => {
    if (location.pathname.includes('/files/tags')) return 'tags';
    if (location.pathname.includes('/files/invitations')) return 'invitations';
    if (location.pathname.includes('/files/members')) return 'members';
    if (location.pathname.includes('/files/roles')) return 'roles';
    if (location.pathname.includes('/files/email-notifications')) return 'email-notifications';
    return searchParams.get('tab') || 'files';
  };

  const activeTab = getActiveTab();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sortBy, setSortBy] = useState('name'); // Default sort by name
  const [sortOrder, setSortOrder] = useState('asc'); // Default ascending order
  
  // Simpler state initialization - just use URL params
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const initialFetchDone = useRef(false);
  const lastFetchedParams = useRef({ folder: null, sortBy: 'name', sortOrder: 'asc', fileTypes: [] });
  const [folderPath, setFolderPath] = useState([]);
  const [specialFilter, setSpecialFilter] = useState(getSpecialFilterFromUrl()); // 'none', 'favorites', 'recent_opened', 'recent_uploaded', 'recycle-bin'

  // Get search term from global window object (set by Layout component)
  const [searchTerm, setSearchTerm] = useState('');
  const [fileTypeFilters, setFileTypeFilters] = useState([]);

  // Sync with global search query and file types from Layout
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.__searchQuery !== undefined && window.__searchQuery !== searchTerm) {
        setSearchTerm(window.__searchQuery);
      }
      if (window.__fileTypes !== undefined && JSON.stringify(window.__fileTypes) !== JSON.stringify(fileTypeFilters)) {
        setFileTypeFilters(window.__fileTypes);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [searchTerm, fileTypeFilters]);
  const [showSpecialFilterDropdown, setShowSpecialFilterDropdown] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [destinationFolder, setDestinationFolder] = useState(null);
  const [folderTree, setFolderTree] = useState([]);
  const [copyName, setCopyName] = useState('');
  const [showDropdown, setShowDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownFile, setDropdownFile] = useState(null);
  const [dataroom, setDataroom] = useState(null);
  const [showAccessLogs, setShowAccessLogs] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedFileForLogs, setSelectedFileForLogs] = useState(null);
  const [showWatermarkSettings, setShowWatermarkSettings] = useState(false);
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedFileForTag, setSelectedFileForTag] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]); // Changed to array for multi-select
  const [loadingTags, setLoadingTags] = useState(false);
  const [watermarkFontSize, setWatermarkFontSize] = useState(14); // Store watermark font size
  const [assigningTags, setAssigningTags] = useState(false);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showEmailPrefs, setShowEmailPrefs] = useState(false);
  const [showImportTemplateModal, setShowImportTemplateModal] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showBulkActionsDropdown, setShowBulkActionsDropdown] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadTracker, setShowUploadTracker] = useState(false);

  // Default column configuration based on getColumnDisplayName
  const defaultColumnOrder = ['index', 'unread', 'type', 'name', 'tags', 'uploadedBy', 'uploadedOn', 'deletedBy', 'deletedAt', 'size', 'pageCount', 'actions'];
  const defaultColumnVisibility = {
    index: true,
    unread: true,
    type: true,
    name: true,
    tags: true,
    uploadedBy: true,
    uploadedOn: true,
    deletedBy: true,
    deletedAt: true,
    size: true,
    pageCount: true,
    actions: true
  };

  const [columnVisibility, setColumnVisibility] = useState(defaultColumnVisibility);
  const [columnOrder, setColumnOrder] = useState(defaultColumnOrder);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const columnOrderRef = useRef(defaultColumnOrder);

  // Load table preferences from API on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        console.log('Loading table preferences from API...');
        console.log('Default column order:', defaultColumnOrder);
        console.log('Default column visibility:', defaultColumnVisibility);

        // Load column order
        const orderResponse = await tablePreferencesService.getColumnOrder('filemanager');
        console.log('Column order response:', orderResponse);

        if (orderResponse?.data?.columnOrder && Array.isArray(orderResponse.data.columnOrder)) {
          // Use saved order from API
          const savedOrder = orderResponse.data.columnOrder;

          // Add any new columns that exist in defaults but not in saved order
          const newColumns = defaultColumnOrder.filter(col => !savedOrder.includes(col));
          const mergedOrder = [...savedOrder, ...newColumns];

          console.log('Setting column order from API:', mergedOrder);
          setColumnOrder(mergedOrder);
          columnOrderRef.current = mergedOrder;
        } else {
          console.log('No saved column order, using defaults');
        }

        // Load column visibility
        const visibilityResponse = await tablePreferencesService.getColumnVisibility('filemanager');
        console.log('Column visibility response:', visibilityResponse);

        if (visibilityResponse?.data?.columnVisibility) {
          // Merge saved visibility with defaults (in case new columns were added)
          const savedVisibility = visibilityResponse.data.columnVisibility;
          const mergedVisibility = { ...defaultColumnVisibility, ...savedVisibility };

          console.log('Setting column visibility from API:', mergedVisibility);
          setColumnVisibility(mergedVisibility);
        } else {
          console.log('No saved column visibility, using defaults');
        }

        console.log('Table preferences loaded successfully');
      } catch (error) {
        console.error('Failed to load table preferences:', error);
        // Continue with defaults if API fails
        console.log('Using default column configuration due to error');
      } finally {
        setPreferencesLoaded(true);
        console.log('✅ Column preferences loaded - preferencesLoaded set to true');
      }
    };

    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showEditDataroomModal, setShowEditDataroomModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [watermarkedPdfUrl, setWatermarkedPdfUrl] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showManagementDropdown, setShowManagementDropdown] = useState(false);
  const [showSelectionHint, setShowSelectionHint] = useState(true); // Show selection hint by default

  // Folder tree sidebar state
  const [showFolderTree, setShowFolderTree] = useState(() => {
    const saved = localStorage.getItem('showFolderTree');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Function to check user permissions
  // Permissions are at the dataroom level from the API response, not on individual files
  const hasPermission = (file, permission) => {
    console.log(file, permission);
    // If user is owner and permissions are not set, grant all permissions
    if (dataroom?.is_owner && (!dataroom.user_permissions || !Array.isArray(dataroom.user_permissions))) {
      return true;
    }

    // Check if dataroom has permissions (permissions apply to all files in the dataroom)
    if (dataroom && dataroom.user_permissions && Array.isArray(dataroom.user_permissions)) {
      // Handle both string array format and object array format
      return dataroom.user_permissions.some(perm => {
        if (typeof perm === 'string') {
          return perm === permission;
        } else if (perm && typeof perm === 'object' && perm.name) {
          return perm.name === permission;
        }
        return false;
      });
    }

    // If no permissions found, return false for restricted actions
    return false;
  };

  // Function to check if user has global permission
  const hasGlobalPermission = (permission) => {
    // If user is owner and permissions are not set, grant all permissions
    if (dataroom?.is_owner && (!dataroom.user_permissions || !Array.isArray(dataroom.user_permissions))) {
      return true;
    }

    // Check dataroom-level permissions from the API response
    if (dataroom && dataroom.user_permissions && Array.isArray(dataroom.user_permissions)) {
      // Handle both string array format and object array format
      return dataroom.user_permissions.some(perm => {
        if (typeof perm === 'string') {
          return perm === permission;
        } else if (perm && typeof perm === 'object' && perm.name) {
          return perm.name === permission;
        }
        return false;
      });
    }

    // If no permissions found, return false
    return false;
  };


  // Function to change tab
  const changeTab = (tab) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (tab === 'files') {
      newSearchParams.delete('tab'); // Remove tab param for default
    } else {
      newSearchParams.set('tab', tab);
    }
    setSearchParams(newSearchParams);
  };

  // Helper function to check if file can be previewed (only PDF and images)
  const canPreviewFile = (file) => {
    const fileName = file.name || file.display_name || '';
    const extension = fileName.split('.').pop().toLowerCase();
    const mimeType = file.mimeType || file.mime_type || '';
    
    // Only allow preview for PDF and image files
    const previewableExtensions = [
      // Images only
      'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico',
      // PDF only
      'pdf'
    ];
    
    // Check if extension is previewable
    if (previewableExtensions.includes(extension)) {
      return true;
    }
    
    // Check if mime type is image or PDF
    if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
      return true;
    }
    
    return false;
  };

  // Sync specialFilter with URL changes
  useEffect(() => {
    // Skip on initial load - let the URL params initialization handle it
    if (!initialLoadComplete) {
      return;
    }

    const newFilter = getSpecialFilterFromUrl();
    if (newFilter !== specialFilter) {
      setSpecialFilter(newFilter);
      // Fetch files based on the new filter
      if (newFilter === 'none') {
        fetchFiles(currentFolder, 'none');
      } else {
        fetchFiles(null, newFilter);
      }
    }
  }, [location.pathname, initialLoadComplete]);

  // Initialize from URL params on mount
  useEffect(() => {
    if (!dataroomUuid) {
      navigate('/file-manager');
      return;
    }

    // Restore state from URL on initial load only
    if (!initialLoadComplete) {
      const folderParam = searchParams.get('folder');
      const pathParam = searchParams.get('path');

      // Clear files immediately to prevent showing stale data
      setFiles([]);
      setLoading(true);

      if (folderParam) {
        setCurrentFolder(folderParam);

        // If path parameter exists, restore breadcrumbs
        if (pathParam) {
          try {
            const pathData = JSON.parse(decodeURIComponent(pathParam));
            setBreadcrumbs(pathData);
          } catch (error) {
            console.error('Error parsing path parameter:', error);
          }
        }

        // Fetch files directly based on URL params
        fetchDataroomDetails();
        fetchFiles(folderParam);
        // Track that we've fetched this folder with these params to prevent double-fetch
        lastFetchedParams.current = { folder: folderParam, sortBy, sortOrder, fileTypes: fileTypeFilters };
      } else {
        // No folder param, load root folder
        setCurrentFolder(null);
        setBreadcrumbs([]);
        fetchDataroomDetails();
        fetchFiles(null);
        // Track that we've fetched root folder with these params
        lastFetchedParams.current = { folder: null, sortBy, sortOrder, fileTypes: fileTypeFilters };
      }

      // Mark as complete - now other useEffects can run
      setInitialLoadComplete(true);
      initialFetchDone.current = true;
    }
  }, [dataroomUuid, navigate, searchParams, initialLoadComplete]);
  
  // Save to sessionStorage for backup
  useEffect(() => {
    if (dataroomUuid && initialLoadComplete && currentFolder !== undefined) {
      const sessionKey = `fileManager-${dataroomUuid}`;
      const stateToSave = {
        currentFolder,
        breadcrumbs,
        timestamp: Date.now()
      };
      sessionStorage.setItem(sessionKey, JSON.stringify(stateToSave));
    }
  }, [currentFolder, breadcrumbs, dataroomUuid, initialLoadComplete]);

  // Fetch files and folders
  const fetchFiles = async (folderUuid = null, filterType = null, forceRefresh = false) => {
    try {
      console.log('📥 fetchFiles called with:', { folderUuid, filterType, forceRefresh, specialFilter });
      setLoading(true);
      let response;

      // Use the filterType parameter if provided, otherwise use the specialFilter state
      const activeFilter = filterType || specialFilter;

      // Handle special filter types
      if (activeFilter === 'recycle-bin') {
        const params = {
          per_page: 100,
          sort_by: sortBy,
          sort_order: sortOrder
        };
        // If we have a folder, include it in the request
        if (folderUuid) {
          params.folder_uuid = folderUuid;
        }
        response = await recycleBinService.getRecycleBinItems(dataroomUuid, params);
        // Handle response structure with 'items' array for recycle bin
        setFiles(response.items || response.data || response.files || []);
        // Don't clear breadcrumbs when navigating folders in recycle bin
        if (!folderUuid) {
          setBreadcrumbs([]);
          setCurrentFolder(null);
        }
      } else if (activeFilter === 'favorites') {
        response = await fileService.getFavoriteFiles(dataroomUuid, {
          per_page: 100,
          sort_by: sortBy,
          sort_order: sortOrder
        });
        setFiles(response.items || response.data || response.files || []);
        // Clear breadcrumbs for special views
        setBreadcrumbs([]);
        setCurrentFolder(null);
      } else if (activeFilter === 'recent_opened') {
        response = await fileService.getRecentlyOpenedFiles(dataroomUuid, {
          per_page: 50,
          sort_by: sortBy,
          sort_order: sortOrder
        });
        setFiles(response.items || response.data || response.files || []);
        // Clear breadcrumbs for special views
        setBreadcrumbs([]);
        setCurrentFolder(null);
      } else if (activeFilter === 'recent_uploaded') {
        response = await fileService.getRecentlyUploadedFiles(dataroomUuid, {
          per_page: 50,
          sort_by: sortBy,
          sort_order: sortOrder
        });
        setFiles(response.items || response.data || response.files || []);
        // Clear breadcrumbs for special views
        setBreadcrumbs([]);
        setCurrentFolder(null);
      } else {
        // Normal file listing
        const params = {
          sort_by: sortBy,
          sort_order: sortOrder,
          forceRefresh: forceRefresh // Add force refresh flag
        };
        if (folderUuid) {
          params.folder_uuid = folderUuid;
        }
        // Add file type filters if any are selected
        if (fileTypeFilters && fileTypeFilters.length > 0) {
          params.file_types = fileTypeFilters;
        }
        response = await fileService.listFiles(dataroomUuid, params);
        const filesData = response.items || response.data || response.files || [];
        console.log('📂 Received files from API:', filesData.length, 'files/folders');
        console.log('📂 Files data:', filesData);
        setFiles(filesData);
      }

      // Store user_permissions from the API response
      if (response.user_permissions) {
        setDataroom(prev => ({
          ...prev,
          user_permissions: response.user_permissions
        }));
      }

      // Set storage stats if available
      if (response.storage_stats) {
        setStorageStats(response.storage_stats);
      }

      // Don't reset breadcrumbs here - they're managed by navigation functions (except for special filters)
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
      console.log('✅ Files loaded - loading set to false');
    }
  };

  // Fetch dataroom details
  const fetchDataroomDetails = async () => {
    if (!dataroomUuid) return;
    
    try {
      // First, ensure watermark font size is loaded from configuration
      await watermarkService.ensureFontSizeLoaded();
      const fontSize = watermarkService.getFontSize();
      setWatermarkFontSize(fontSize);
      
      const response = await dataroomService.getDataroomDetails(dataroomUuid);
      setDataroom(response.data || response);
      // Convert watermark_enabled (0/1) to boolean
      const watermarkStatus = response.data?.watermark_enabled !== undefined 
        ? response.data.watermark_enabled 
        : (response?.watermark_enabled !== undefined ? response.watermark_enabled : 0);
      const isEnabled = watermarkStatus === 1 || watermarkStatus === true || watermarkStatus === '1';
      setWatermarkEnabled(isEnabled);
    } catch (error) {
    }
  };

  // Fetch files when folder or filters change (after initial load is complete)
  useEffect(() => {
    // Only run after initial load from URL params is complete
    if (!initialLoadComplete || !initialFetchDone.current) {
      return;
    }

    // Skip if we've already fetched with these exact params (prevents double-fetch on initial load)
    const paramsMatch =
      currentFolder === lastFetchedParams.current.folder &&
      sortBy === lastFetchedParams.current.sortBy &&
      sortOrder === lastFetchedParams.current.sortOrder &&
      JSON.stringify(fileTypeFilters) === JSON.stringify(lastFetchedParams.current.fileTypes);

    if (paramsMatch) {
      return; // Already fetched with these exact parameters
    }

    if (dataroomUuid) {
      fetchDataroomDetails();
      fetchFiles(currentFolder);
      // Track the parameters we just fetched with
      lastFetchedParams.current = { folder: currentFolder, sortBy, sortOrder, fileTypes: fileTypeFilters };
    }
  }, [currentFolder, dataroomUuid, initialLoadComplete, sortBy, sortOrder, fileTypeFilters]);

  // Switch to 'files' tab if current tab is not available for non-owners
  useEffect(() => {
    if (dataroom?.is_owner === false) {
      // If user is not owner and current tab is one of the restricted tabs
      if (activeTab === 'invitations' || activeTab === 'roles' || activeTab === 'members') {
        // Use setTimeout to avoid potential infinite loops
        const timer = setTimeout(() => {
          changeTab('files');
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [dataroom?.is_owner]);

  // Handle escape key to close dropdowns/modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowDropdown(null);
        setContextMenu(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Save folder tree preferences to localStorage
  useEffect(() => {
    localStorage.setItem('showFolderTree', JSON.stringify(showFolderTree));
  }, [showFolderTree]);

  // Auto-hide folder tree on small screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Auto-hide folder tree when screen width is less than 1024px
      if (width < 1024 && showFolderTree) {
        setShowFolderTree(false);
      }
    };

    // Check on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showFolderTree]);
  
  // Prevent print and save shortcuts when preview is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (previewFile) {
        // Prevent Ctrl+P (print)
        if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
          e.preventDefault();
          toast('Printing is disabled for security reasons', { icon: '⚠️' });
          return false;
        }
        // Prevent Ctrl+S (save)
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault();
          toast('Saving is disabled for security reasons', { icon: '⚠️' });
          return false;
        }
        // Prevent F12 (dev tools)
        if (e.key === 'F12') {
          e.preventDefault();
          return false;
        }
        // Prevent Ctrl+Shift+I (dev tools)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
          e.preventDefault();
          return false;
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewFile]);
  
  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.actions-dropdown')) {
        setShowActionsDropdown(false);
      }
      if (showSpecialFilterDropdown && !event.target.closest('.special-filter-dropdown')) {
        setShowSpecialFilterDropdown(false);
      }
      if (showBulkActionsDropdown && !event.target.closest('.bulk-actions-dropdown')) {
        setShowBulkActionsDropdown(false);
      }
      if (showColumnSettings && !event.target.closest('.column-settings-dropdown')) {
        setShowColumnSettings(false);
      }
    };

    if (showActionsDropdown || showSpecialFilterDropdown || showBulkActionsDropdown || showColumnSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionsDropdown, showSpecialFilterDropdown, showBulkActionsDropdown, showColumnSettings]);

  // Close dropdown when clicking outside or scrolling (but not when scrolling inside dropdown)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is outside dropdown and trigger button
      if (showDropdown && 
          !e.target.closest('.dropdown-menu') && 
          !e.target.closest('[data-dropdown-trigger]')) {
        setShowDropdown(null);
      }
    };

    const handleScroll = (e) => {
      // Don't close if scrolling inside the dropdown menu
      if (showDropdown && !e.target.closest('.dropdown-menu')) {
        setShowDropdown(null);
      }
    };

    const handleResize = () => {
      if (showDropdown) {
        setShowDropdown(null);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showDropdown]);

  // Handle special filter changes
  useEffect(() => {
    // Skip on initial load - let the URL params initialization handle it
    if (!initialLoadComplete) {
      return;
    }

    if (specialFilter === 'favorites' || specialFilter === 'recent_opened' || specialFilter === 'recent_uploaded') {
      // Fetch special filtered files
      fetchFiles(null, specialFilter);
    } else if (specialFilter === 'none') {
      // For normal view, fetch current folder
      fetchFiles(currentFolder);
    }
  }, [specialFilter, initialLoadComplete]);

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      try {
        // Set upload progress
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 0
        }));

        // Simulate progress (in real app, use axios onUploadProgress)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const currentProgress = prev[file.name] || 0;
            if (currentProgress >= 90) {
              clearInterval(progressInterval);
              return prev;
            }
            return {
              ...prev,
              [file.name]: currentProgress + 10
            };
          });
        }, 200);

        const response = await fileService.uploadFile(dataroomUuid, file, currentFolder);
        
        // Complete progress
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 100
        }));

        // Remove progress after delay
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
        }, 1000);

        toast.success(`${file.name} uploaded successfully`);
        fetchFiles(currentFolder);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }
    }
  }, [currentFolder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true
  });

  // Create new folder
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required');
      return;
    }

    try {
      await fileService.createFolder(dataroomUuid, newFolderName, currentFolder);
      toast.success('Folder created successfully');
      setShowNewFolderModal(false);
      setNewFolderName('');
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  // Rename file/folder
  const handleRename = async () => {
    if (!newFileName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await fileService.renameFile(dataroomUuid, fileToRename.uuid || fileToRename.id, newFileName.trim());
      toast.success('Renamed successfully');
      setShowRenameModal(false);
      setFileToRename(null);
      setNewFileName('');
      fetchFiles(currentFolder);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to rename';
      toast.error(errorMessage);
    }
  };

  // Delete file/folder
  const confirmDelete = (file) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };
  
  const handleDelete = async () => {
    if (!fileToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      await fileService.deleteFile(dataroomUuid, fileToDelete.uuid || fileToDelete.id);
      toast.success('Deleted successfully');
      setShowDeleteModal(false);
      setFileToDelete(null);
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete multiple files
  const handleDeleteMultiple = async () => {
    if (selectedFiles.length === 0 || isDeleting) return;

    setIsDeleting(true);
    const toastId = toast.loading(`Deleting ${selectedFiles.length} file(s)...`);
    try {
      const result = await fileService.deleteMultipleFiles(dataroomUuid, selectedFiles);

      toast.success(`${selectedFiles.length} file(s) deleted successfully`, { id: toastId });

      // Clear selection and refresh
      setSelectedFiles([]);
      setShowBulkDeleteModal(false);
      fetchFiles(currentFolder);
    } catch (error) {
      console.error('Error deleting files:', error);
      toast.error('Failed to delete files', { id: toastId });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle restore from recycle bin
  const handleRestoreFile = async (file) => {
    const toastId = toast.loading('Restoring file...');
    try {
      await recycleBinService.restoreFile(dataroomUuid, file.uuid || file.id);
      toast.success('File restored successfully', { id: toastId });
      // Refresh the recycle bin view
      if (specialFilter === 'recycle-bin') {
        fetchFiles(null, 'recycle-bin');
      }
    } catch (error) {
      console.error('Error restoring file:', error);
      toast.error(error.response?.data?.message || 'Failed to restore file', { id: toastId });
    }
  };

  // Handle restore multiple files
  const handleRestoreMultiple = async () => {
    if (selectedFiles.length === 0) return;

    const toastId = toast.loading(`Restoring ${selectedFiles.length} file(s)...`);
    try {
      const result = await recycleBinService.restoreMultipleFiles(dataroomUuid, selectedFiles);

      if (result.totalRestored > 0) {
        toast.success(`${result.totalRestored} file(s) restored successfully`, { id: toastId });
      }

      if (result.totalFailed > 0) {
        toast.error(`${result.totalFailed} file(s) failed to restore`);
      }

      // Clear selection and refresh
      setSelectedFiles([]);
      if (specialFilter === 'recycle-bin') {
        fetchFiles(null, 'recycle-bin');
      }
    } catch (error) {
      console.error('Error restoring files:', error);
      toast.error('Failed to restore files', { id: toastId });
    }
  };

  // Handle permanently delete (if needed in future)
  const handlePermanentDelete = async (file) => {
    const confirmed = window.confirm('Are you sure you want to permanently delete this file? This action cannot be undone.');
    if (!confirmed) return;

    const toastId = toast.loading('Permanently deleting...');
    try {
      await recycleBinService.permanentlyDelete(dataroomUuid, file.uuid || file.id);
      toast.success('File permanently deleted', { id: toastId });
      if (specialFilter === 'recycle-bin') {
        fetchFiles(null, 'recycle-bin');
      }
    } catch (error) {
      console.error('Error permanently deleting file:', error);
      toast.error(error.response?.data?.message || 'Failed to permanently delete', { id: toastId });
    }
  };

  // Handle copy file/folder
  const handleCopy = async (file) => {
    setSelectedFile(file);
    setCopyName(file.name);
    
    // Use existing files list to get folders
    try {
      
      // Always fetch fresh folder list for accuracy
      const response = await fileService.getAllFolders(dataroomUuid);
      setFolderTree(response.data || response || []);
      setShowCopyModal(true);
    } catch (error) {
      toast.error(`Failed to load folders: ${error.response?.data?.message || error.message}`);
    }
  };

  // Handle move file/folder
  const handleMove = async (file) => {
    setSelectedFile(file);
    
    // Use existing files list to get folders
    try {
      
      // Always fetch fresh folder list for accuracy
      const response = await fileService.getAllFolders(dataroomUuid);
      setFolderTree(response.data || response || []);
      setShowMoveModal(true);
    } catch (error) {
      toast.error(`Failed to load folders: ${error.response?.data?.message || error.message}`);
    }
  };

  // Execute copy operation
  const executeCopy = async () => {
    if (!selectedFile) return;
    
    try {
      await fileService.copyFile(dataroomUuid, selectedFile.uuid || selectedFile.id, destinationFolder);
      toast.success('Copied successfully');
      setShowCopyModal(false);
      setSelectedFile(null);
      setDestinationFolder(null);
      setCopyName('');
      fetchFiles(currentFolder);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to copy';
      toast.error(errorMessage);
    }
  };

  // Execute move operation
  const executeMove = async () => {
    if (!selectedFile) return;
    
    try {
      await fileService.moveFile(dataroomUuid, selectedFile.uuid || selectedFile.id, destinationFolder);
      toast.success('Moved successfully');
      setShowMoveModal(false);
      setSelectedFile(null);
      setDestinationFolder(null);
      
      // If we moved the current folder we're in, navigate to parent or root
      if (selectedFile.uuid === currentFolder || selectedFile.id === currentFolder) {
        if (breadcrumbs.length > 1) {
          navigateToBreadcrumb(breadcrumbs.length - 2);
        } else {
          navigateToBreadcrumb(-1);
        }
      } else {
        fetchFiles(currentFolder);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to move';
      toast.error(errorMessage);
    }
  };

  // Mark file as read
  const handleMarkAsRead = async (file) => {
    try {
      await fileService.markAsRead(dataroomUuid, file.uuid || file.id);
      toast.success(`"${file.name || file.display_name}" marked as read`);
      // Refresh the file list to update the read status, staying in current folder
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to mark file as read');
    }
  };

  // Mark file as unread
  const handleMarkAsUnread = async (file) => {
    try {
      await fileService.markAsUnread(dataroomUuid, file.uuid || file.id);
      toast.success(`"${file.name || file.display_name}" marked as unread`);
      // Refresh the file list to update the read status, staying in current folder
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to mark file as unread');
    }
  };

  // Bulk mark files as read
  const handleBulkMarkAsRead = async () => {
    if (selectedFiles.length === 0) return;
    
    const toastId = toast.loading(`Marking ${selectedFiles.length} files as read...`);
    try {
      await fileService.markAsReadBulk(dataroomUuid, selectedFiles);
      toast.success(`${selectedFiles.length} files marked as read`, { id: toastId });
      setSelectedFiles([]);
      // Refresh files in current folder
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to mark files as read', { id: toastId });
    }
  };

  // Bulk mark files as unread
  const handleBulkMarkAsUnread = async () => {
    if (selectedFiles.length === 0) return;
    
    const toastId = toast.loading(`Marking ${selectedFiles.length} files as unread...`);
    try {
      await fileService.markAsUnreadBulk(dataroomUuid, selectedFiles);
      toast.success(`${selectedFiles.length} files marked as unread`, { id: toastId });
      setSelectedFiles([]);
      // Refresh files in current folder
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to mark files as unread', { id: toastId });
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Select all files and folders
  const selectAllFiles = () => {
    const allFileIds = filteredFiles
      .map(file => file.uuid || file.id);
    setSelectedFiles(allFileIds);
  };

  // Deselect all files
  const deselectAllFiles = () => {
    setSelectedFiles([]);
  };

  // Add file to favorites
  const handleAddToFavorite = async (file) => {
    try {
      await fileService.addToFavorite(dataroomUuid, file.uuid || file.id);
      toast.success(`"${file.name || file.display_name}" added to favorites`);
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to add to favorites');
    }
  };

  // Remove file from favorites
  const handleRemoveFromFavorite = async (file) => {
    try {
      await fileService.removeFromFavorite(dataroomUuid, file.uuid || file.id);
      toast.success(`"${file.name || file.display_name}" removed from favorites`);
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  // Bulk add files to favorites
  const handleBulkAddToFavorite = async () => {
    if (selectedFiles.length === 0) return;
    
    const toastId = toast.loading(`Adding ${selectedFiles.length} files to favorites...`);
    try {
      await fileService.addToFavoriteBulk(dataroomUuid, selectedFiles);
      toast.success(`${selectedFiles.length} files added to favorites`, { id: toastId });
      setSelectedFiles([]);
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to add to favorites', { id: toastId });
    }
  };

  // Bulk remove files from favorites
  const handleBulkRemoveFromFavorite = async () => {
    if (selectedFiles.length === 0) return;
    
    const toastId = toast.loading(`Removing ${selectedFiles.length} files from favorites...`);
    try {
      await fileService.removeFromFavoriteBulk(dataroomUuid, selectedFiles);
      toast.success(`${selectedFiles.length} files removed from favorites`, { id: toastId });
      setSelectedFiles([]);
      fetchFiles(currentFolder);
    } catch (error) {
      toast.error('Failed to remove from favorites', { id: toastId });
    }
  };

  // Download file using the download endpoint
  const handleDownload = async (file) => {
    const toastId = toast.loading('Preparing download...');

    try {
      // Automatically mark file as read when downloading
      if (file.is_read === false) {
        fileService.markAsRead(dataroomUuid, file.uuid || file.id)
          .then(() => {
            // Refresh the file list to update the read status
            fetchFiles(currentFolder);
          })
          .catch(err => console.error('Failed to mark as read:', err));
      }

      // Log for audit trail
      const downloadInfo = {
        fileName: file.name || file.display_name,
        fileType: file.mimeType || file.mime_type || 'unknown',
        downloadedBy: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
        email: user?.email || '',
        timestamp: new Date().toLocaleString(),
        dataroomUuid: dataroomUuid,
        fileUuid: file.uuid || file.id
      };

      // Store audit info
      const downloads = JSON.parse(sessionStorage.getItem('fileDownloads') || '[]');
      downloads.push(downloadInfo);
      sessionStorage.setItem('fileDownloads', JSON.stringify(downloads));

      const fileName = file.name || file.display_name || 'file';

      // Use the fileService triggerDownload method which calls the download endpoint
      // GET /api/datarooms/{dataroomUuid}/files/{fileUuid}/download
      await fileService.triggerDownload(dataroomUuid, file.uuid || file.id, fileName);

      toast.dismiss(toastId);
      toast.success(`Download started: ${fileName}`, { duration: 3000 });
    } catch (error) {
      console.error('Download error:', error);
      toast.dismiss(toastId);

      // Show error with retry option
      toast.error(
        <div>
          <div className="font-semibold">Download Failed</div>
          <div className="text-xs mt-1">Unable to download: {file.name || file.display_name}</div>
          <div className="text-xs text-gray-500 mt-1">{error.message || 'Unknown error'}</div>
          <button
            onClick={() => handleDownload(file)}
            className="text-xs mt-2 underline hover:no-underline"
          >
            Click to retry
          </button>
        </div>,
        { duration: 5000 }
      );
    }
  };

  // View file with watermark for preview
  const handleView = async (file) => {
    let loadingToast = null;
    try {
      // For folders, navigate to them instead
      if (file.type === 'folder') {
        navigateToFolder(file);
        return;
      }
      
      // Automatically mark file as read when previewing
      if (file.is_read === false) {
        fileService.markAsRead(dataroomUuid, file.uuid || file.id)
          .then(() => {
            // Refresh the file list to update the read status
            fetchFiles(currentFolder);
          })
      }

      // Check file type
      const mimeType = file.mimeType || file.mime_type || 'application/octet-stream';
      const fileName = file.name || file.display_name || '';

      const isPDF = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
      const isImage = mimeType.startsWith('image/') || fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);

      // For images and PDFs, show preview
      if (isPDF || isImage) {
        loadingToast = toast.loading('Loading preview...');

        try {
          // Call the preview API endpoint to get a URL for inline viewing (not forced download)
          const previewResponse = await fileService.previewFile(dataroomUuid, file.uuid || file.id);
          const previewUrl = previewResponse.url || previewResponse.preview_url || previewResponse.download_url;

          if (!previewUrl) {
            toast.dismiss(loadingToast);
            toast.error('Preview URL not available');
            return;
          }

          if (isPDF) {
            // For PDFs, set up PDF viewer
            setLoadingPdf(false);
            setPdfBlobUrl(previewUrl);

            const previewData = {
              ...file,
              url: previewUrl,
              mimeType: mimeType,
              needsPDFViewer: true,
              fileName: fileName,
              isBlobUrl: true
            };
            setPreviewFile(previewData);
          } else if (isImage) {
            // For images, set up image preview
            const previewData = {
              ...file,
              url: previewUrl,
              mimeType: mimeType,
              fileName: fileName,
              isBlobUrl: true
            };
            setPreviewFile(previewData);
          }

          toast.dismiss(loadingToast);
        } catch (error) {
          console.error('Preview error:', error);
          toast.dismiss(loadingToast);
          toast.error('Failed to load preview');
        }
      } else {
        // For all other file types (Excel, Word, ZIP, etc.), download directly
        await handleDownload(file);
      }
    } catch (error) {
      console.error('Error in handleView:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error('Failed to process file');
    }
  };

  // Handle column header click for sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      // If clicking the same column, toggle the order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as the new sort field with ascending order
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = async (columnKey) => {
    const updatedVisibility = {
      ...columnVisibility,
      [columnKey]: !columnVisibility[columnKey]
    };
    setColumnVisibility(updatedVisibility);

    // Save to API
    try {
      console.log('Saving column visibility to API:', updatedVisibility);
      await tablePreferencesService.updateTablePreferences('filemanager', {
        columnOrder,
        columnVisibility: updatedVisibility
      });
      console.log('Column visibility saved successfully');
    } catch (error) {
      console.error('Failed to save column visibility:', error);
      toast.error('Failed to save column preferences');
    }
  };

  // Handle drag start
  const handleDragStart = (e, columnKey) => {
    // Prevent dragging actions column
    if (columnKey === 'actions') {
      e.preventDefault();
      return;
    }
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  // Handle drag over
  const handleDragOver = (e, columnKey) => {
    e.preventDefault();

    // Prevent dropping on actions column
    if (columnKey === 'actions') {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    e.dataTransfer.dropEffect = 'move';

    if (draggedColumn === null || draggedColumn === columnKey || draggedColumn === 'actions') return;

    const draggedIdx = columnOrder.indexOf(draggedColumn);
    const targetIdx = columnOrder.indexOf(columnKey);

    if (draggedIdx === targetIdx) return;

    const newOrder = [...columnOrder];
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumn);

    console.log('Reordering columns:', { from: draggedColumn, to: columnKey, newOrder });
    setColumnOrder(newOrder);
    columnOrderRef.current = newOrder; // Update ref with latest order
  };

  // Handle drag end - save the reordered columns to API
  const handleDragEnd = async () => {
    const draggedCol = draggedColumn;
    setDraggedColumn(null);

    // Only save if something was actually dragged
    if (!draggedCol) return;

    // Save to API using ref (which has the latest order)
    try {
      const currentOrder = columnOrderRef.current;
      console.log('Saving column order to API after drag:', currentOrder);

      await tablePreferencesService.updateTablePreferences('filemanager', {
        columnOrder: currentOrder,
        columnVisibility
      });
      console.log('Column order saved successfully');
      toast.success('Column order saved');
    } catch (error) {
      console.error('Failed to save column order:', error);
      toast.error('Failed to save column preferences');
    }
  };

  // Get column display name
  const getColumnDisplayName = (columnKey) => {
    const names = {
      index: 'Index',
      unread: 'Unread',
      type: 'Type',
      name: 'Name',
      tags: 'Tags',
      uploadedBy: 'Uploaded By',
      uploadedOn: 'Uploaded On',
      deletedBy: 'Deleted By',
      deletedAt: 'Deleted At',
      size: 'Size',
      pageCount: 'Page Count',
      actions: 'Actions'
    };
    return names[columnKey] || columnKey;
  };

  // Render table cell for a specific column and file
  const renderColumnCell = (columnKey, file, Icon, color) => {
    if (!columnVisibility[columnKey]) return null;

    // Skip columns that don't apply to current view
    if (specialFilter !== 'recycle-bin' && ['deletedBy', 'deletedAt'].includes(columnKey)) {
      return null;
    }
    if (specialFilter === 'recycle-bin' && ['unread', 'uploadedOn', 'size', 'pageCount'].includes(columnKey)) {
      return null;
    }

    switch (columnKey) {
      case 'index':
        return (
          <td key="index" className="px-3 py-1.5 text-sm text-gray-600">
            {file.index_number || '-'}
          </td>
        );

      case 'unread':
        return (
          <td key="unread" className="text-center px-3 py-1.5">
            {file.type !== 'folder' && file.is_read === false && (
              <div className="inline-flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-blue-600 rounded-full shadow-sm ring-1 ring-white" title="Unread"></div>
              </div>
            )}
          </td>
        );

      case 'type':
        return (
          <td key="type" className="text-center px-3 py-1.5">
            <div className="inline-flex items-center justify-center">
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
          </td>
        );

      case 'name':
        return (
          <td key="name" className="px-3 py-1.5 text-sm">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm truncate max-w-xs ${
                  file.type !== 'folder' && file.is_read === false
                    ? 'font-medium text-gray-900'
                    : 'font-medium text-gray-700'
                }`}
                title={file.name || file.display_name}
              >
                {file.name || file.display_name}
                {file.type === 'folder' && file.file_count !== undefined && file.file_count !== null && (
                  <span className="text-gray-500 font-normal ml-1">
                    ({file.file_count})
                  </span>
                )}
                {file.type !== 'folder' && file.is_favorite && (
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 inline-block ml-1.5 -mt-0.5" />
                )}
              </span>
            </div>
          </td>
        );

      case 'tags':
        return (
          <td key="tags" className="px-3 py-1.5 text-sm hidden lg:table-cell">
            {file.tags && file.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {file.tags.slice(0, 2).map(tag => (
                  <span key={tag.id} className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                    {tag.name}
                  </span>
                ))}
                {file.tags.length > 2 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    +{file.tags.length - 2}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </td>
        );

      case 'uploadedBy':
        return (
          <td key="uploadedBy" className="px-3 py-1.5 text-sm text-gray-600 hidden md:table-cell">
            {(() => {
              const user = file.created_by || file.uploaded_by || file.owner || file.user;
              if (user) {
                if (typeof user === 'string') return user;
                return user.name ||
                       user.full_name ||
                       `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                       user.email ||
                       '-';
              }
              return '-';
            })()}
          </td>
        );

      case 'uploadedOn':
        return (
          <td key="uploadedOn" className="px-3 py-1.5 text-sm text-gray-600 hidden md:table-cell">
            {(() => {
              const date = new Date(file.created_at || file.updated_at);
              const month = date.toLocaleDateString('en-US', { month: 'short' });
              const day = date.getDate();
              const year = date.getFullYear();
              const hours = date.getHours();
              const minutes = date.getMinutes();
              const ampm = hours >= 12 ? 'PM' : 'AM';
              const displayHours = hours % 12 || 12;
              const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
              return `${month} ${day}, ${year} ${displayHours}:${displayMinutes} ${ampm}`;
            })()}
          </td>
        );

      case 'deletedBy':
        return (
          <td key="deletedBy" className="px-3 py-1.5 text-sm text-gray-600 hidden md:table-cell">
            {file.deleted_by ? (
              `${file.deleted_by.first_name || ''} ${file.deleted_by.last_name || ''}`.trim() || '-'
            ) : '-'}
          </td>
        );

      case 'deletedAt':
        return (
          <td key="deletedAt" className="px-3 py-1.5 text-sm text-red-600 hidden md:table-cell">
            {(() => {
              if (file.deleted_at) {
                const date = new Date(file.deleted_at);
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const day = date.getDate();
                const year = date.getFullYear();
                return `${month} ${day}, ${year}`;
              }
              return '-';
            })()}
          </td>
        );

      case 'size':
        return (
          <td key="size" className="px-3 py-1.5 text-sm text-gray-600 hidden sm:table-cell">
            {file.type === 'folder' ? '-' : (file.formatted_size || file.size)}
          </td>
        );

      case 'pageCount':
        return (
          <td key="pageCount" className="px-3 py-1.5 text-sm text-gray-600 text-center hidden lg:table-cell">
            {file.type === 'folder' ? '-' : (file.metadata?.pages || '-')}
          </td>
        );

      case 'actions':
        return null; // Actions column is rendered separately below

      default:
        return null;
    }
  };

  // Render table header for a specific column
  const renderColumnHeader = (columnKey) => {
    if (!columnVisibility[columnKey]) return null;

    // Skip columns that don't apply to current view
    if (specialFilter !== 'recycle-bin' && ['deletedBy', 'deletedAt'].includes(columnKey)) {
      return null;
    }
    if (specialFilter === 'recycle-bin' && ['unread', 'uploadedOn', 'size', 'pageCount'].includes(columnKey)) {
      return null;
    }

    switch (columnKey) {
      case 'index':
        return (
          <th
            key="index"
            className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('index_number')}
          >
            <div className="flex items-center gap-1">
              Index
              <div className="flex flex-col items-center justify-center -space-y-1">
                <ChevronUp className={`w-3 h-3 ${sortBy === 'index_number' && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
                <ChevronDown className={`w-3 h-3 ${sortBy === 'index_number' && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
              </div>
            </div>
          </th>
        );

      case 'unread':
        return <th key="unread" className="text-center px-3 py-1.5 text-sm font-semibold text-gray-900">Unread</th>;

      case 'type':
        return <th key="type" className="text-center px-3 py-1.5 text-sm font-semibold text-gray-900">Type</th>;

      case 'name':
        return (
          <th
            key="name"
            className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('name')}
          >
            <div className="flex items-center gap-1">
              Name
              <div className="flex flex-col items-center justify-center -space-y-1">
                <ChevronUp className={`w-3 h-3 ${sortBy === 'name' && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
                <ChevronDown className={`w-3 h-3 ${sortBy === 'name' && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
              </div>
            </div>
          </th>
        );

      case 'tags':
        return <th key="tags" className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden lg:table-cell">Tags</th>;

      case 'uploadedBy':
        return <th key="uploadedBy" className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden md:table-cell">Uploaded By</th>;

      case 'uploadedOn':
        return (
          <th
            key="uploadedOn"
            className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden md:table-cell cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('uploaded_at')}
          >
            <div className="flex items-center gap-1">
              Uploaded On
              <div className="flex flex-col items-center justify-center -space-y-1">
                <ChevronUp className={`w-3 h-3 ${sortBy === 'uploaded_at' && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
                <ChevronDown className={`w-3 h-3 ${sortBy === 'uploaded_at' && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
              </div>
            </div>
          </th>
        );

      case 'deletedBy':
        return <th key="deletedBy" className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden md:table-cell">Deleted By</th>;

      case 'deletedAt':
        return (
          <th
            key="deletedAt"
            className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden md:table-cell cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('deleted_at')}
          >
            <div className="flex items-center gap-1">
              Deleted At
              <div className="flex flex-col items-center justify-center -space-y-1">
                <ChevronUp className={`w-3 h-3 ${sortBy === 'deleted_at' && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
                <ChevronDown className={`w-3 h-3 ${sortBy === 'deleted_at' && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
              </div>
            </div>
          </th>
        );

      case 'size':
        return (
          <th
            key="size"
            className="text-left px-3 py-1.5 text-sm font-semibold text-gray-900 hidden sm:table-cell cursor-pointer hover:bg-gray-100 select-none"
            onClick={() => handleSort('size')}
          >
            <div className="flex items-center gap-1">
              Size
              <div className="flex flex-col items-center justify-center -space-y-1">
                <ChevronUp className={`w-3 h-3 ${sortBy === 'size' && sortOrder === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
                <ChevronDown className={`w-3 h-3 ${sortBy === 'size' && sortOrder === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
              </div>
            </div>
          </th>
        );

      case 'pageCount':
        return <th key="pageCount" className="text-center px-3 py-1.5 text-sm font-semibold text-gray-900 hidden lg:table-cell">Page Count</th>;

      case 'actions':
        return <th key="actions" className="text-right px-3 py-1.5 text-sm font-semibold text-gray-900">Actions</th>;

      default:
        return null;
    }
  };

  // Navigate to folder
  const navigateToFolder = (folder) => {
    // Don't reset special filter if we're in recycle bin - we want to stay in recycle bin view
    if (specialFilter !== 'none' && specialFilter !== 'recycle-bin') {
      setSpecialFilter('none');
    }

    const folderId = folder.uuid || folder.id;
    setCurrentFolder(folderId);
    setSelectedFiles([]);

    // Update breadcrumbs - add the folder to the path
    const newBreadcrumbs = [...breadcrumbs, {
      id: folderId,
      name: folder.name,
      display_name: folder.display_name,
      uuid: folder.uuid
    }];
    setBreadcrumbs(newBreadcrumbs);

    // Update URL with folder state - use replace to avoid creating extra history entries
    const params = new URLSearchParams(searchParams);
    params.set('folder', folderId);
    params.set('path', encodeURIComponent(JSON.stringify(newBreadcrumbs)));
    setSearchParams(params, { replace: true });
  };

  // Handle export dataroom index
  const handleExportIndex = async () => {
    const toastId = toast.loading('Exporting dataroom index...');
    try {
      const result = await fileService.exportDataroomIndex(dataroomUuid);
      toast.success(`Index exported successfully: ${result.filename}`, { id: toastId });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.data?.message || 'Failed to export dataroom index', { id: toastId });
    }
  };

  // Handle export files by scope
  const handleExportFilesByScope = async (scope) => {
    const scopeLabels = {
      'all': 'all files',
      'favorites': 'favorite files',
      'recently_opened': 'recently opened files',
      'recently_uploaded': 'recently uploaded files'
    };

    const toastId = toast.loading(`Exporting ${scopeLabels[scope] || 'files'}...`);
    try {
      const result = await fileService.exportFilesByScope(dataroomUuid, scope);
      toast.success(`Export completed: ${result.filename}`, { id: toastId });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.data?.message || `Failed to export ${scopeLabels[scope] || 'files'}`, { id: toastId });
    }
  };

  // Handle export files/folders as ZIP
  const handleExportAsZip = async (uuids = null) => {
    const toastId = toast.loading(uuids ? 'Exporting selected files as ZIP...' : 'Exporting all files as ZIP...');
    try {
      const response = await exportService.exportFilesAsZip(dataroomUuid, uuids);

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = exportService.extractFilenameFromContentDisposition(contentDisposition);

      // Use default filename if extraction failed
      if (!filename) {
        filename = 'export.zip';
      }

      // Download the file
      exportService.downloadBlob(response.data, filename);
      toast.success(`Export completed: ${filename}`, { id: toastId });
    } catch (error) {
      console.error('Export ZIP failed:', error);
      toast.error(error.response?.data?.message || 'Failed to export files as ZIP', { id: toastId });
    }
  };

  // Handle opening import modal
  const handleOpenImportModal = async () => {
    setShowImportTemplateModal(true);
  };

  // Handle updating dataroom
  const handleUpdateDataroom = async (updatedData) => {
    const toastId = toast.loading('Updating dataroom...');
    try {
      // Create FormData for the API call
      const formData = new FormData();
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] !== null && updatedData[key] !== undefined && updatedData[key] !== '') {
          formData.append(key, updatedData[key]);
        }
      });

      const response = await dataroomService.updateDataroom(dataroomUuid, formData);
      toast.success('Dataroom updated successfully', { id: toastId });
      // Refresh dataroom details
      fetchDataroomDetails();
      setShowEditDataroomModal(false);
      return response;
    } catch (error) {
      console.error('Failed to update dataroom:', error);
      toast.error(error.response?.data?.message || 'Failed to update dataroom', { id: toastId });
      throw error;
    }
  };

  // Handle importing template
  const handleImportTemplate = async (template) => {
    const toastId = toast.loading('Importing template...');

    try {
      // Use the new import endpoint for dataroom owners
      // Pass current folder as target so template is imported to current location
      const response = await dataroomTemplateService.importTemplate(
        dataroomUuid,
        template.id,
        currentFolder // Import to current folder instead of root
      );

      toast.success(response.message || 'Template imported successfully!', { id: toastId });
      fetchFiles(currentFolder); // Refresh the file list in current folder
      return response;
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.message || 'Failed to import template', { id: toastId });
      throw error;
    }
  };

  // Fetch available tags for dataroom
  const fetchAvailableTags = async () => {
    try {
      setLoadingTags(true);
      // Use getTags for admin users to get all dataroom tags
      const response = await tagService.getTags(dataroomUuid, {
        tag_type: 'file', // Filter for file tags only
        per_page: 100 // Get all tags
      });
      setAvailableTags(response.data || []);
      
      // Also fetch the current file's tags if it exists
      if (selectedFileForTag && selectedFileForTag.tags) {
      }
    } catch (error) {
      toast.error('Failed to load tags');
      setAvailableTags([]); // Set empty array on error
    } finally {
      setLoadingTags(false);
    }
  };

  // Handle tag assignment - modified for multiple tags
  const handleAssignTags = async () => {
    if (selectedTagIds.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    setAssigningTags(true);
    const fileId = selectedFileForTag.uuid || selectedFileForTag.id;
    let successCount = 0;
    let errorCount = 0;

    try {
      // Assign each selected tag
      for (const tagId of selectedTagIds) {
        try {
          await fileService.assignTagToFile(dataroomUuid, fileId, tagId);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} tag(s) assigned successfully`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} tag(s) failed to assign`);
      }

      setShowTagModal(false);
      setSelectedFileForTag(null);
      setSelectedTagIds([]);
      fetchFiles(currentFolder); // Refresh files to show updated tags
    } catch (error) {
      toast.error('Failed to assign tags');
    } finally {
      setAssigningTags(false);
    }
  };

  // Toggle tag selection
  const toggleTagSelection = (tagId) => {
    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  // Navigate using breadcrumb
  const navigateToBreadcrumb = (index) => {
    // Reset special filters when navigating via breadcrumbs
    if (specialFilter !== 'none') {
      setSpecialFilter('none');
    }

    const params = new URLSearchParams(searchParams);

    if (index === -1) {
      // Navigate to root
      setCurrentFolder(null);
      setBreadcrumbs([]);
      // Clear URL parameters for root folder
      params.delete('folder');
      params.delete('path');
    } else {
      // Navigate to specific folder in breadcrumb
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      const targetFolder = newBreadcrumbs[newBreadcrumbs.length - 1];
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(targetFolder.id);
      // Update URL parameters
      params.set('folder', targetFolder.id);
      params.set('path', encodeURIComponent(JSON.stringify(newBreadcrumbs)));
    }
    
    setSearchParams(params, { replace: true }); // Use replace to avoid creating extra history entries
    setSelectedFiles([]);
  };

  // Get file icon based on type with proper icon components
  const getFileIcon = (file) => {
    if (file.type === 'folder') return { Icon: Folder, color: 'text-blue-500' };
    
    const fileName = file.name || file.display_name || '';
    const extension = fileName.split('.').pop().toLowerCase();
    
    // Check MIME type first, then fall back to extension
    const mimeType = file.mimeType || file.mime_type || '';
    
    // PDF files
    if (mimeType === 'application/pdf' || extension === 'pdf') {
      return { Icon: FaFilePdf, color: 'text-red-600' };
    }
    
    // Word documents
    if (mimeType.includes('word') || ['doc', 'docx', 'odt'].includes(extension)) {
      return { Icon: FaFileWord, color: 'text-blue-700' };
    }
    
    // Excel files
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || ['xls', 'xlsx'].includes(extension)) {
      return { Icon: FaFileExcel, color: 'text-green-700' };
    }
    
    // CSV files
    if (extension === 'csv') {
      return { Icon: FaFileCsv, color: 'text-green-600' };
    }
    
    // PowerPoint files
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation') || ['ppt', 'pptx', 'odp'].includes(extension)) {
      return { Icon: FaFilePowerpoint, color: 'text-orange-600' };
    }
    
    // Images by specific type
    if (['jpg', 'jpeg'].includes(extension)) {
      return { Icon: FileImage, color: 'text-blue-500' };
    }
    if (extension === 'png') {
      return { Icon: FileImage, color: 'text-purple-500' };
    }
    if (extension === 'svg') {
      return { Icon: FileImage, color: 'text-pink-500' };
    }
    if (extension === 'gif') {
      return { Icon: FileImage, color: 'text-indigo-500' };
    }
    if (['bmp', 'webp', 'ico', 'tiff'].includes(extension)) {
      return { Icon: FaFileImage, color: 'text-purple-600' };
    }
    
    // Videos
    if (['mp4', 'webm'].includes(extension)) {
      return { Icon: FileVideo, color: 'text-red-500' };
    }
    if (['avi', 'mkv', 'mov', 'wmv', 'flv', 'm4v', 'mpg', 'mpeg'].includes(extension)) {
      return { Icon: FaFileVideo, color: 'text-pink-600' };
    }
    
    // Audio
    if (['mp3', 'm4a', 'aac'].includes(extension)) {
      return { Icon: FileAudio, color: 'text-green-500' };
    }
    if (['wav', 'ogg', 'flac', 'wma', 'aiff'].includes(extension)) {
      return { Icon: FaFileAudio, color: 'text-indigo-600' };
    }
    
    // Archives
    if (extension === 'zip') {
      return { Icon: FileArchive, color: 'text-yellow-600' };
    }
    if (['rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'].includes(extension)) {
      return { Icon: FaFileArchive, color: 'text-amber-600' };
    }
    
    // Programming languages with specific icons
    if (['js', 'mjs'].includes(extension)) {
      return { Icon: SiJavascript, color: 'text-yellow-500' };
    }
    if (['ts', 'tsx'].includes(extension)) {
      return { Icon: SiTypescript, color: 'text-blue-600' };
    }
    if (extension === 'jsx') {
      return { Icon: SiReact, color: 'text-cyan-500' };
    }
    if (['py', 'pyc', 'pyw'].includes(extension)) {
      return { Icon: SiPython, color: 'text-blue-500' };
    }
    if (extension === 'java') {
      return { Icon: FaFileCode, color: 'text-red-600' };
    }
    if (['cpp', 'cc', 'cxx'].includes(extension)) {
      return { Icon: SiCplusplus, color: 'text-blue-700' };
    }
    if (['c', 'h'].includes(extension)) {
      return { Icon: SiC, color: 'text-blue-800' };
    }
    if (extension === 'cs') {
      return { Icon: FaFileCode, color: 'text-purple-700' };
    }
    if (extension === 'php') {
      return { Icon: SiPhp, color: 'text-indigo-600' };
    }
    if (extension === 'rb') {
      return { Icon: SiRuby, color: 'text-red-700' };
    }
    if (extension === 'go') {
      return { Icon: SiGo, color: 'text-cyan-600' };
    }
    if (extension === 'rs') {
      return { Icon: SiRust, color: 'text-orange-700' };
    }
    if (['swift', 'playground'].includes(extension)) {
      return { Icon: FaFileCode, color: 'text-orange-500' };
    }
    
    // Web files
    if (['html', 'htm'].includes(extension)) {
      return { Icon: SiHtml5, color: 'text-orange-600' };
    }
    if (extension === 'css') {
      return { Icon: SiCss3, color: 'text-blue-600' };
    }
    if (extension === 'vue') {
      return { Icon: SiVuedotjs, color: 'text-green-600' };
    }
    
    // Data files
    if (extension === 'json') {
      return { Icon: FileJson2, color: 'text-yellow-600' };
    }
    if (extension === 'xml') {
      return { Icon: FileCode2, color: 'text-orange-600' };
    }
    if (['yaml', 'yml'].includes(extension)) {
      return { Icon: FileCode2, color: 'text-red-600' };
    }
    if (['sql', 'db', 'sqlite'].includes(extension)) {
      return { Icon: Database, color: 'text-blue-700' };
    }
    
    // Text/Document files
    if (extension === 'txt') {
      return { Icon: FileText, color: 'text-gray-600' };
    }
    if (extension === 'md') {
      return { Icon: FileType, color: 'text-gray-700' };
    }
    if (extension === 'log') {
      return { Icon: Terminal, color: 'text-gray-500' };
    }
    if (['rtf', 'tex', 'wpd'].includes(extension)) {
      return { Icon: FaFileAlt, color: 'text-gray-600' };
    }
    
    // Contract/Legal files
    if (['contract', 'agreement', 'nda'].some(term => fileName.toLowerCase().includes(term))) {
      return { Icon: FaFileContract, color: 'text-indigo-700' };
    }
    
    // Invoice files
    if (['invoice', 'receipt', 'bill'].some(term => fileName.toLowerCase().includes(term))) {
      return { Icon: FaFileInvoice, color: 'text-green-700' };
    }
    
    // Signature files
    if (['signature', 'signed'].some(term => fileName.toLowerCase().includes(term))) {
      return { Icon: FaFileSignature, color: 'text-blue-800' };
    }
    
    // Config/System files
    if (['ini', 'cfg', 'conf', 'config', 'env'].includes(extension)) {
      return { Icon: FileKey, color: 'text-gray-700' };
    }
    
    // Package files
    if (['deb', 'rpm', 'dmg', 'pkg', 'exe', 'msi', 'app', 'apk', 'ipa'].includes(extension)) {
      return { Icon: Package, color: 'text-purple-600' };
    }
    
    // Default file icon
    return { Icon: File, color: 'text-gray-500' };
  };

  // Handle context menu
  const handleContextMenu = (e, file) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      file
    });
  };

  // Close context menu and dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      // Don't close if clicking on dropdown menu or its children
      if (!e.target.closest('.dropdown-menu')) {
        setShowDropdown(null);
      }
      // Don't close management dropdown if clicking on it or its children
      if (!e.target.closest('.management-dropdown')) {
        setShowManagementDropdown(false);
      }
      // Always close context menu on any click
      setContextMenu(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any blob URLs when component unmounts
      if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, []);
  
  // Clean up PDF blob URL when preview closes
  useEffect(() => {
    // Only clean up when preview is closed (no previewFile)
    if (!previewFile && pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  }, [previewFile, pdfBlobUrl]);

  // Filter files based on search and type
  // Note: Search filtering is now handled only through the header search suggestions dropdown
  // The table displays all files without client-side filtering
  const filteredFiles = files;

  return (
    <div className="h-full flex flex-col overflow-hidden" {...getRootProps()}>
      <input {...getInputProps()} />
      
      {/* Header - Minimal Design */}
      <div className="bg-white border-b flex-shrink-0">
        <div className="">
          <div className="flex items-center justify-between px-4">
            <div className="flex items-center gap-2">
              {/* Removed Back arrow and text as per request */}
            </div>

            <div className="flex items-center gap-4">
            </div>
          </div>
        </div>

      </div>

      {/* Files Tab Content */}
      {activeTab === 'files' && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Folder Tree Sidebar - Collapsible (Hidden with CSS to prevent unmount/remount) */}
          <div
            className="border-r border-gray-200 bg-white"
            style={{
              width: showFolderTree ? '255px' : '0',
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.2s ease',
              height: '100vh'
            }}
          >
            <div style={{ width: '255px', height: '100%' }}>
              <FolderTreeSidebar
                dataroomUuid={dataroomUuid}
                currentFolderUuid={currentFolder}
                isOwner={dataroom?.is_owner}
                storageStats={storageStats}
                onFolderSelect={(folderUuid, folderObject, breadcrumbPath) => {
                  console.log('FileManager - onFolderSelect received:', {
                    folderUuid,
                    folderObject,
                    breadcrumbPath
                  });

                  if (folderUuid === null) {
                    // Navigating to root - clear breadcrumbs and folder
                    setCurrentFolder(null);
                    setBreadcrumbs([]);
                    setSpecialFilter('none');

                    // Navigate to regular files view (remove special filters from URL)
                    navigate(`/datarooms/${dataroomUuid}/files`, { replace: true });

                    // Fetch root files
                    fetchFiles(null);
                  } else {
                    // Navigating to a specific folder from sidebar
                    setCurrentFolder(folderUuid);
                    setBreadcrumbs(breadcrumbPath || []); // Set breadcrumbs from the folder tree
                    setSpecialFilter('none');

                    console.log('Setting breadcrumbs to:', breadcrumbPath);

                    // Navigate to regular files view with folder parameter and path
                    const params = new URLSearchParams();
                    params.set('folder', folderUuid);
                    if (breadcrumbPath && breadcrumbPath.length > 0) {
                      params.set('path', encodeURIComponent(JSON.stringify(breadcrumbPath)));
                    }
                    navigate(`/datarooms/${dataroomUuid}/files?${params.toString()}`, { replace: true });

                    // Refresh files when folder is selected
                    fetchFiles(folderUuid);
                  }
                }}
              />
            </div>
          </div>


          {/* Main Files Area */}
          <div className="flex-1 overflow-auto">
            <div>
            {/* Persistent Action Bar - Always visible */}
            <div className="bg-white border-b border-gray-200" style={{ padding: '0.5rem 1rem' }}>
              <div className="flex items-center justify-between">
            {/* Folder Tree Toggle Button */}
            <button
              onClick={() => setShowFolderTree(!showFolderTree)}
              className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors mr-2"
              title={showFolderTree ? "Hide Folder Tree" : "Show Folder Tree"}
            >
              {showFolderTree ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
            </button>

            {/* Bulk Actions Button - Show when files are selected (After toggle) */}
            {selectedFiles.length > 0 && (
              <>
                <div className="relative bulk-actions-dropdown">
                  <button
                    onClick={() => setShowBulkActionsDropdown(!showBulkActionsDropdown)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <span>Actions ({selectedFiles.length})</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {showBulkActionsDropdown && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {specialFilter !== 'recycle-bin' && (
                          <>
                            <button
                              onClick={() => {
                                handleBulkMarkAsRead();
                                setShowBulkActionsDropdown(false);
                              }}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <BookOpen className="w-4 h-4 mr-3" />
                              Mark as Read
                            </button>
                            <button
                              onClick={() => {
                                handleBulkMarkAsUnread();
                                setShowBulkActionsDropdown(false);
                              }}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <BookX className="w-4 h-4 mr-3" />
                              Mark as Unread
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            <button
                              onClick={() => {
                                handleBulkAddToFavorite();
                                setShowBulkActionsDropdown(false);
                              }}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Star className="w-4 h-4 mr-3" />
                              Add to Favorites
                            </button>
                            <button
                              onClick={() => {
                                handleBulkRemoveFromFavorite();
                                setShowBulkActionsDropdown(false);
                              }}
                              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <StarOff className="w-4 h-4 mr-3" />
                              Remove from Favorites
                            </button>
                          </>
                        )}
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={() => {
                            setShowBulkDeleteModal(true);
                            setShowBulkActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-3" />
                          Delete ({selectedFiles.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Separate Download Button with File Size */}
                {hasGlobalPermission('download') && (
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={() => handleExportAsZip(selectedFiles)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                    <span className="text-xs text-gray-600 font-medium">
                      {(() => {
                        const totalSize = selectedFiles.reduce((sum, fileId) => {
                          const file = files.find(f => (f.uuid || f.id) === fileId);
                          return sum + (parseInt(file?.size) || 0);
                        }, 0);

                        // Format bytes to human readable
                        const formatBytes = (bytes) => {
                          if (bytes === 0) return '0 B';
                          const k = 1024;
                          const sizes = ['B', 'KB', 'MB', 'GB'];
                          const i = Math.floor(Math.log(bytes) / Math.log(k));
                          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                        };

                        return formatBytes(totalSize);
                      })()}
                    </span>
                  </div>
                )}
              </>
            )}

            <div className="flex-1"></div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* New Folder Button - Hide in recycle bin */}
              {specialFilter !== 'recycle-bin' && (hasGlobalPermission('create-folder') || !dataroom?.user_permissions) && (
                <button
                  onClick={() => setShowNewFolderModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <FolderPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Folder</span>
                </button>
              )}

              {/* Upload Button - Hide in recycle bin */}
              {specialFilter !== 'recycle-bin' && (hasGlobalPermission('upload') || !dataroom?.user_permissions) && (
                <label className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Upload</span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => onDrop(Array.from(e.target.files))}
                  />
                </label>
              )}


              {/* Restore Selected Button - Show in recycle bin when files are selected */}
              {specialFilter === 'recycle-bin' && selectedFiles.length > 0 && (
                <button
                  onClick={handleRestoreMultiple}
                  className="flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                >
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span className="hidden lg:inline">Restore Selected ({selectedFiles.length})</span>
                  <span className="lg:hidden">Restore ({selectedFiles.length})</span>
                </button>
              )}

              {/* Upload Tracker Button */}
              {getCounts().total > 0 && (
                <button
                  onClick={() => setShowUploadTracker(!showUploadTracker)}
                  className="relative flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
                  {getCounts().uploading > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-medium text-white">
                      {getCounts().uploading}
                    </span>
                  )}
                  <span className="hidden lg:inline ml-2">Uploads</span>
                </button>
              )}

              {/* Special Filters Dropdown - HIDDEN */}
              <div className="relative special-filter-dropdown" style={{ display: 'none' }}>
                <button
                  onClick={() => setShowSpecialFilterDropdown(!showSpecialFilterDropdown)}
                  className="flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Filter className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Quick Filters</span>
                  <ChevronDown className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                </button>

                {showSpecialFilterDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setSpecialFilter('favorites');
                          setShowSpecialFilterDropdown(false);
                        }}
                        className={`flex items-center w-full px-3 py-1.5 text-sm hover:bg-gray-100 ${
                          specialFilter === 'favorites' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <Star className="w-4 h-4 mr-3" />
                        Favorite Files
                      </button>
                      <button
                        onClick={() => {
                          setSpecialFilter('recent_opened');
                          setShowSpecialFilterDropdown(false);
                        }}
                        className={`flex items-center w-full px-3 py-1.5 text-sm hover:bg-gray-100 ${
                          specialFilter === 'recent_opened' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <Clock className="w-4 h-4 mr-3" />
                        Recently Opened
                      </button>
                      <button
                        onClick={() => {
                          setSpecialFilter('recent_uploaded');
                          setShowSpecialFilterDropdown(false);
                        }}
                        className={`flex items-center w-full px-3 py-1.5 text-sm hover:bg-gray-100 ${
                          specialFilter === 'recent_uploaded' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <Upload className="w-4 h-4 mr-3" />
                        Recently Uploaded
                      </button>
                      {specialFilter !== 'none' && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={() => {
                              setSpecialFilter('none');
                              setShowSpecialFilterDropdown(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <X className="w-4 h-4 mr-3" />
                            Clear Filter
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions Dropdown */}
              <div className="relative actions-dropdown">
                <button
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  className="flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="hidden sm:inline">Advanced Options</span>
                  <ChevronDown className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                </button>

                {showActionsDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {/* Import Template - Only for owners */}
                      {dataroom?.is_owner && (
                        <button
                          onClick={() => {
                            handleOpenImportModal();
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileInput className="w-4 h-4 mr-3" />
                          Import Template
                        </button>
                      )}

                      {/* Upload Files & Folder - Available to users with upload permission */}
                      {hasGlobalPermission('upload') && !inRecycleBinMode && (
                        <button
                          onClick={() => {
                            setShowUploadModal(true);
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FaFileUpload className="w-4 h-4 mr-3" />
                          Upload Files & Folder
                        </button>
                      )}

                      {/* Export Index - Available to all */}
                      <button
                        onClick={() => {
                          handleExportIndex();
                          setShowActionsDropdown(false);
                        }}
                        className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <FaFileExport className="w-4 h-4 mr-3" />
                        Export Dataroom Index
                      </button>

                      {/* Export Files by Scope - Available to all */}
                      {/* Export based on current filter */}
                      {specialFilter !== 'none' && (
                        <button
                          onClick={() => {
                            const scopeMap = {
                              'favorites': 'favorites',
                              'recent_opened': 'recently_opened',
                              'recent_uploaded': 'recently_uploaded'
                            };
                            handleExportFilesByScope(scopeMap[specialFilter]);
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FaFileExport className="w-4 h-4 mr-3" />
                          Export Current View
                        </button>
                      )}

                      {/* Export submenu for scopes - Available to all */}
                      {specialFilter === 'none' && (
                        <>
                          <button
                            onClick={() => {
                              handleExportFilesByScope('favorites');
                              setShowActionsDropdown(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Star className="w-4 h-4 mr-3" />
                            Export Favourites
                          </button>
                          <button
                            onClick={() => {
                              handleExportFilesByScope('recently_opened');
                              setShowActionsDropdown(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Clock className="w-4 h-4 mr-3" />
                            Export Recently Viewed
                          </button>
                          <button
                            onClick={() => {
                              handleExportFilesByScope('recently_uploaded');
                              setShowActionsDropdown(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Upload className="w-4 h-4 mr-3" />
                            Export Recent Uploads
                          </button>
                        </>
                      )}

                      {/* Download - Requires download permission */}
                      {hasGlobalPermission('download') && (
                        <>
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={() => {
                              // Export selected files if any, otherwise export all
                              if (selectedFiles.length > 0) {
                                handleExportAsZip(selectedFiles);
                              } else {
                                handleExportAsZip(null);
                              }
                              setShowActionsDropdown(false);
                            }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <Download className="w-4 h-4 mr-3" />
                            {selectedFiles.length > 0
                              ? `Download ${selectedFiles.length} Selected`
                              : 'Download All Files'}
                          </button>
                        </>
                      )}

                      {/* Divider before owner-only options */}
                      {dataroom?.is_owner && (
                        <div className="border-t border-gray-100 my-1"></div>
                      )}

                      {/* Watermark Settings - Only for owners */}
                      {dataroom?.is_owner && (
                        <button
                          onClick={() => {
                            setShowWatermarkSettings(true);
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Droplets className="w-4 h-4 mr-3" />
                          Watermark Settings
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Column Settings Dropdown */}
              <div className="relative column-settings-dropdown">
                <button
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  className="flex items-center px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  title="Column Settings"
                >
                  <Menu className="w-4 h-4" />
                </button>

                {showColumnSettings && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1.5 px-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900">Column Settings</h3>
                      <p className="text-xs text-gray-500 mt-1">Show/hide and reorder columns</p>
                    </div>
                    <div className="py-2 max-h-96 overflow-y-auto">
                      {columnOrder.map((columnKey, index) => {
                        // Skip columns that don't apply to current filter
                        if (specialFilter !== 'recycle-bin' && ['deletedBy', 'deletedAt'].includes(columnKey)) {
                          return null;
                        }
                        if (specialFilter === 'recycle-bin' && ['unread', 'uploadedOn', 'size', 'pageCount'].includes(columnKey)) {
                          return null;
                        }

                        const isRequired = ['name', 'actions'].includes(columnKey);
                        const isDragging = draggedColumn === columnKey;
                        const isActionsColumn = columnKey === 'actions';
                        const isDraggable = !isActionsColumn;

                        return (
                          <div
                            key={columnKey}
                            draggable={isDraggable}
                            onDragStart={(e) => isDraggable && handleDragStart(e, columnKey)}
                            onDragOver={(e) => isDraggable && handleDragOver(e, columnKey)}
                            onDragEnd={isDraggable ? handleDragEnd : undefined}
                            className={`flex items-center gap-3 px-3 py-1.5 transition-all ${
                              isActionsColumn
                                ? 'bg-gray-50 cursor-default'
                                : isDragging
                                  ? 'opacity-50 bg-blue-100 cursor-move'
                                  : isRequired
                                    ? 'bg-gray-50 cursor-move'
                                    : 'hover:bg-gray-50 cursor-move'
                            }`}
                          >
                            {/* Drag Handle */}
                            <div className="flex-shrink-0">
                              {isActionsColumn ? (
                                <div className="w-4 h-4"></div>
                              ) : (
                                <GripVertical className="w-4 h-4 text-gray-400" />
                              )}
                            </div>

                            {/* Checkbox and Label */}
                            <label className={`flex items-center ${isRequired ? 'cursor-not-allowed' : 'cursor-pointer'} flex-1`}>
                              <input
                                type="checkbox"
                                checked={columnVisibility[columnKey] !== undefined ? columnVisibility[columnKey] : true}
                                onChange={() => !isRequired && toggleColumnVisibility(columnKey)}
                                disabled={isRequired}
                                className={`mr-3 rounded text-blue-600 focus:ring-blue-500 ${isRequired ? 'opacity-50' : ''}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-sm text-gray-700">
                                {getColumnDisplayName(columnKey)}
                                {isRequired && !isActionsColumn && <span className="text-xs text-gray-500 ml-1">(Required)</span>}
                                {isActionsColumn && <span className="text-xs text-gray-500 ml-1">(Required, Fixed Position)</span>}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

            </div>
              </div>
            </div>


        {/* Main Content */}
        <div className="flex-1 overflow-visible bg-gray-50 relative" style={{ minHeight: 0, overflowY: 'auto' }}>

        {/* Breadcrumbs Before Table - No Box */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-1 sm:gap-2 text-sm">
            {specialFilter === 'recycle-bin' ? (
              <div className="flex items-center gap-2 text-gray-700">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="font-medium">Recycle Bin</span>
              </div>
            ) : specialFilter === 'favorites' ? (
              <div className="flex items-center gap-2 text-gray-700">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="font-medium">Favorite Files</span>
              </div>
            ) : specialFilter === 'recent_opened' ? (
              <div className="flex items-center gap-2 text-gray-700">
                <Eye className="w-4 h-4 text-green-600" />
                <span className="font-medium">Recently Viewed</span>
              </div>
            ) : specialFilter === 'recent_uploaded' ? (
              <div className="flex items-center gap-2 text-gray-700">
                <Clock className="w-4 h-4 text-purple-600" />
                <span className="font-medium">Recently Uploaded</span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => navigateToBreadcrumb(-1)}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium transition-colors"
                  title="Go to Root Folder"
                >
                  <Home className="w-4 h-4" />
                  <span>Home</span>
                </button>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id || index}>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <button
                      onClick={() => navigateToBreadcrumb(index)}
                      className={`${
                        index === breadcrumbs.length - 1
                          ? 'text-gray-900 font-semibold'
                          : 'text-blue-600 hover:text-blue-800'
                      } transition-colors`}
                      title={crumb.display_name || crumb.name}
                    >
                      {crumb.display_name || crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Bulk Actions Toolbar - HIDDEN - Using dropdown in action bar instead */}
        <AnimatePresence>
        {false && selectedFiles.length > 0 && specialFilter !== 'recycle-bin' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 px-3 py-1.5 text-sm shadow-md">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="text-xs sm:text-sm text-gray-600">
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={deselectAllFiles}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1 w-full sm:w-auto">
              {specialFilter !== 'recycle-bin' && (
                // Normal file actions
                <>
                  <button
                    onClick={handleBulkMarkAsRead}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
                  >
                    <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Mark as Read</span>
                    <span className="sm:hidden">Read</span>
                  </button>
                  <button
                    onClick={handleBulkMarkAsUnread}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
                  >
                    <BookX className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Mark as Unread</span>
                    <span className="sm:hidden">Unread</span>
                  </button>
                  <div className="hidden sm:block h-6 w-px bg-gray-300 mx-1"></div>
                  <button
                    onClick={handleBulkAddToFavorite}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
                  >
                    <Star className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Add to Favorites</span>
                    <span className="sm:hidden">Favorite</span>
                  </button>
                  <button
                    onClick={handleBulkRemoveFromFavorite}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
                  >
                    <StarOff className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Remove from Favorites</span>
                    <span className="sm:hidden">Unfavorite</span>
                  </button>
                </>
              )}
              {hasGlobalPermission('download') && (
                <>
                  <div className="hidden sm:block h-6 w-px bg-gray-300 mx-1"></div>
                  <button
                    onClick={() => handleExportAsZip(selectedFiles)}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Download</span>
                    <span className="sm:hidden">Download</span>
                  </button>
                </>
              )}
              <div className="hidden sm:block h-6 w-px bg-gray-300 mx-1"></div>
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="px-2 sm:px-3 py-1 sm:py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Delete ({selectedFiles.length})</span>
                <span className="sm:hidden">Delete ({selectedFiles.length})</span>
              </button>
            </div>
          </div>
        </motion.div>
        )}
        </AnimatePresence>
        {/* Drop Zone Overlay */}
        {isDragActive && (
          <div className="fixed inset-0 bg-primary-100 bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <Upload className="w-16 h-16 text-primary-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Drop files here to upload</p>
            </div>
          </div>
        )}

        {/* Filter Results Info */}
        {specialFilter !== 'none' && (
          <div className="py-1.5 px-3 bg-gray-50 border-b">
            <p className="text-xs sm:text-sm text-gray-600">
              Showing {filteredFiles.length} items
              {specialFilter === 'favorites' && <span> (favorite files)</span>}
              {specialFilter === 'recent_opened' && <span> (recently opened files)</span>}
              {specialFilter === 'recent_uploaded' && <span> (recently uploaded files)</span>}
            </p>
          </div>
        )}

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="mb-4 space-y-2">
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <div key={fileName} className="bg-white rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{fileName}</span>
                  <span className="text-sm text-gray-500">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Files Grid/List */}
        <div className="py-4 px-4">
          {(() => {
            const showSkeleton = loading || !preferencesLoaded;
            if (showSkeleton) {
              console.log('⏳ Showing skeleton - loading:', loading, 'preferencesLoaded:', preferencesLoaded);
            }
            return showSkeleton;
          })() ? (
            <TableLoader rows={8} columns={6} />
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Folder className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-4" />
              <p className="text-sm sm:text-base text-gray-500">No files or folders found</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-10 px-3 py-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={filteredFiles.length > 0 &&
                               filteredFiles.every(f => selectedFiles.includes(f.uuid || f.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllFiles();
                        } else {
                          deselectAllFiles();
                        }
                      }}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  {columnOrder.map(columnKey => renderColumnHeader(columnKey))}
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => {
                  const { Icon, color } = getFileIcon(file);
                  return (
                    <tr
                      key={file.uuid || file.id}
                      className={`border-b cursor-pointer transition-all duration-200 ${
                        showDropdown === (file.uuid || file.id)
                          ? 'bg-blue-50 border-blue-400'
                          : file.type !== 'folder' && file.is_read === false
                          ? 'bg-blue-100/70 border-blue-300 hover:bg-blue-200/70'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={(e) => {
                        // Single click for selection (with ctrl/cmd for multi-select)
                        e.stopPropagation();
                        if (e.ctrlKey || e.metaKey) {
                          // Multi-select with ctrl/cmd for both files and folders
                          toggleFileSelection(file.uuid || file.id);
                        } else if (file.type === 'folder') {
                          // For folders, navigate on single click (if not ctrl/cmd)
                          handleView(file);
                        } else {
                          // Single select for files - clear others and select this one
                          setSelectedFiles([file.uuid || file.id]);
                        }
                      }}
                      onDoubleClick={() => {
                        // Double click to preview/open
                        handleView(file);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, file)}
                    >
                      <td className="px-3 py-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.uuid || file.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleFileSelection(file.uuid || file.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </td>
                      {/* Render columns in custom order */}
                      {columnOrder.map(columnKey => renderColumnCell(columnKey, file, Icon, color))}
                      <td className="px-3 py-1.5 text-sm text-right relative">
                        <button
                          data-dropdown-trigger={file.uuid || file.id}
                          onClick={(e) => {
                            e.stopPropagation();

                            // Store the file data for the dropdown
                            setDropdownFile(file);

                            // Get button position when clicked
                            const rect = e.currentTarget.getBoundingClientRect();
                            const dropdownWidth = 200;
                            // Different heights for different menu types: recycle bin (60), folder (60), file (500)
                            const dropdownHeight = specialFilter === 'recycle-bin' ? 60 : (file.type === 'folder' ? 60 : 500);

                            let newPosition = {
                              // For table rows, align with row center
                              // Adjust vertical offset based on menu size
                              top: rect.top + (rect.height / 2) - (dropdownHeight < 100 ? 20 : 40),
                              left: rect.left - dropdownWidth - 10 // Position to the left of the button
                            };

                            // If positioning to the left goes off screen, position to the right of button
                            if (newPosition.left < 10) {
                              newPosition.left = rect.right + 10;
                            }

                            // Adjust if dropdown would go off right edge
                            if (newPosition.left + dropdownWidth > window.innerWidth - 10) {
                              newPosition.left = Math.max(10, window.innerWidth - dropdownWidth - 10);
                            }

                            // Keep dropdown within vertical viewport bounds with better bottom positioning
                            const availableSpaceBelow = window.innerHeight - rect.bottom;
                            const availableSpaceAbove = rect.top;

                            if (newPosition.top < 10) {
                              newPosition.top = 10;
                            }

                            // If not enough space below, position above or adjust to fit
                            if (availableSpaceBelow < dropdownHeight && availableSpaceAbove > availableSpaceBelow) {
                              // Position above the button
                              newPosition.top = Math.max(10, rect.top - dropdownHeight);
                            } else if (newPosition.top + dropdownHeight > window.innerHeight - 10) {
                              // Position at bottom of viewport with some padding
                              newPosition.top = Math.max(10, window.innerHeight - dropdownHeight - 10);
                            }

                            // Ensure dropdown stays within viewport bounds
                            newPosition.left = Math.max(10, Math.min(newPosition.left, window.innerWidth - dropdownWidth - 10));
                            newPosition.top = Math.max(10, Math.min(newPosition.top, window.innerHeight - dropdownHeight - 10));

                            setDropdownPosition(newPosition);
                            setShowDropdown(showDropdown === (file.uuid || file.id) ? null : (file.uuid || file.id));
                          }}
                          onDoubleClick={(e) => e.stopPropagation()}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>
                        
                        {/* Dropdown menu - List view dropdown positioned outside table */}
                        {showDropdown === (file.uuid || file.id) && dropdownFile && (
                          <div
                            className="dropdown-menu fixed bg-white rounded-lg shadow-2xl py-1 min-w-[200px] border border-gray-300 overflow-y-auto"
                            style={{
                              zIndex: 999999,
                              left: `${dropdownPosition.left}px`,
                              top: `${dropdownPosition.top}px`,
                              maxHeight: `${Math.min(500, window.innerHeight - dropdownPosition.top - 20)}px`
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            {specialFilter === 'recycle-bin' ? (
                              // Recycle Bin - Show only Restore option
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestoreFile(dropdownFile);
                                  setShowDropdown(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-green-600"
                              >
                                <RefreshCw className="w-3 h-3" />
                                Restore
                              </button>
                            ) : (
                              <>
                            {/* Folder-specific options */}
                            {dropdownFile.type === 'folder' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleView(dropdownFile);
                                  setShowDropdown(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                              >
                                <FolderOpen className="w-3 h-3" />
                                Open Folder
                              </button>
                            ) : (
                              <>
                                {/* File-specific options - Preview and Download based on permissions */}
                                {canPreviewFile(dropdownFile) && hasPermission(dropdownFile, 'view') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleView(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Eye className="w-3 h-3" />
                                    Preview
                                  </button>
                                )}
                                {hasPermission(dropdownFile, 'download') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Download className="w-3 h-3" />
                                    Download
                                  </button>
                                )}
                                {/* Show Access Logs and Statistics only if user is owner */}
                                {dataroom?.is_owner && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFileForLogs(dropdownFile);
                                        setShowAccessLogs(true);
                                        setShowDropdown(null);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Activity className="w-3 h-3" />
                                      Access Logs
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFileForLogs(dropdownFile);
                                        setShowStatistics(true);
                                        setShowDropdown(null);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <BarChart3 className="w-3 h-3" />
                                      Statistics
                                    </button>
                                  </>
                                )}
                                <hr className="my-1" />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFileForTag(dropdownFile);
                                    fetchAvailableTags();
                                    setShowTagModal(true);
                                    setShowDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <Tag className="w-3 h-3" />
                                  Assign Tags
                                </button>
                                <hr className="my-1" />
                                {/* Mark as Read/Unread Options */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(dropdownFile);
                                    setShowDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <BookOpen className="w-3 h-3 text-green-600" />
                                  <span className="flex-1">Mark as Read</span>
                                  {dropdownFile.is_read === true && (
                                    <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">Current</span>
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsUnread(dropdownFile);
                                    setShowDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <BookX className="w-3 h-3 text-gray-500" />
                                  <span className="flex-1">Mark as Unread</span>
                                  {dropdownFile.is_read === false && (
                                    <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">Current</span>
                                  )}
                                </button>
                                <hr className="my-1" />
                                {/* Favorite options */}
                                {dropdownFile.is_favorite ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveFromFavorite(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <StarOff className="w-3 h-3 text-gray-500" />
                                    <span>Remove from Favorites</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddToFavorite(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Star className="w-3 h-3 text-yellow-500" />
                                    <span>Add to Favorites</span>
                                  </button>
                                )}
                              </>
                            )}
                            {/* Actions that require permissions */}
                            {(hasPermission(dropdownFile, 'copy') || hasPermission(dropdownFile, 'move') ||
                              hasPermission(dropdownFile, 'rename') || hasPermission(dropdownFile, 'delete')) && (
                              <hr className="my-1" />
                            )}
                            {hasPermission(dropdownFile, 'copy') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(dropdownFile);
                                  setShowDropdown(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Copy className="w-3 h-3" />
                                Copy
                              </button>
                            )}
                            {hasPermission(dropdownFile, 'move') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMove(dropdownFile);
                                  setShowDropdown(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Move className="w-3 h-3" />
                                Move
                              </button>
                            )}
                            {hasPermission(dropdownFile, 'rename') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFileToRename(dropdownFile);
                                  setNewFileName(dropdownFile.name);
                                  setShowRenameModal(true);
                                  setShowDropdown(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Edit3 className="w-3 h-3" />
                                Rename
                              </button>
                            )}
                            {specialFilter === 'recycle-bin' ? (
                              // Show Restore option in recycle bin
                              <>
                                <hr className="my-1" />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRestoreFile(dropdownFile);
                                    setShowDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-green-600"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  Restore
                                </button>
                                {hasPermission(dropdownFile, 'delete') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePermanentDelete(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Permanently Delete
                                  </button>
                                )}
                              </>
                            ) : (
                              // Show Delete option in normal view
                              hasPermission(dropdownFile, 'delete') && (
                                <>
                                  <hr className="my-1" />
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      confirmDelete(dropdownFile);
                                      setShowDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    Delete
                                  </button>
                                </>
                              )
                            )}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
        </div>
        </div>

        {/* Context Menu */}
        {contextMenu && (
        <div
          className={`fixed bg-white rounded-lg shadow-2xl py-2 min-w-[200px] border border-gray-300 ${
            specialFilter === 'recycle-bin' ? 'max-h-[60px]' : 'overflow-y-auto'
          }`}
          style={{
            zIndex: 99999999,
            left: (() => {
              const menuWidth = 200;
              // Check if menu would go off the right edge
              if (contextMenu.x + menuWidth > window.innerWidth - 20) {
                return Math.max(10, contextMenu.x - menuWidth);
              }
              return contextMenu.x;
            })(),
            top: (() => {
              // Smaller height for recycle bin (only has Restore option)
              const menuHeight = specialFilter === 'recycle-bin' ? 50 : 500;

              // For recycle bin, center the small menu with the click point
              if (specialFilter === 'recycle-bin') {
                // Center the menu vertically on the click point
                let adjustedY = contextMenu.y - 20;

                // Ensure it doesn't go off screen
                if (adjustedY < 10) adjustedY = 10;
                if (adjustedY + menuHeight > window.innerHeight - 10) {
                  adjustedY = window.innerHeight - menuHeight - 10;
                }
                return adjustedY;
              }

              // For normal menu, check available space
              const availableSpaceBelow = window.innerHeight - contextMenu.y;
              const availableSpaceAbove = contextMenu.y;

              // If not enough space below, position above or adjust to fit
              if (availableSpaceBelow < menuHeight && availableSpaceAbove > availableSpaceBelow) {
                // Position above the click point
                return Math.max(10, contextMenu.y - menuHeight);
              } else if (contextMenu.y + menuHeight > window.innerHeight - 20) {
                // Position at bottom of viewport with some padding
                return Math.max(10, window.innerHeight - menuHeight - 20);
              }
              return contextMenu.y;
            })(),
            maxHeight: specialFilter === 'recycle-bin'
              ? '60px'
              : `${Math.min(500, window.innerHeight - Math.max(10, contextMenu.y) - 20)}px`
          }}
        >
          {specialFilter === 'recycle-bin' ? (
            // Recycle Bin - Show only Restore option
            <button
              onClick={() => {
                handleRestoreFile(contextMenu.file);
                setContextMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-green-600"
            >
              <RefreshCw className="w-4 h-4" />
              Restore
            </button>
          ) : (
            // Normal context menu options
            <>
          {contextMenu.file.type !== 'folder' && (
            <>
              {canPreviewFile(contextMenu.file) && hasPermission(contextMenu.file, 'view') && (
                <button
                  onClick={() => {
                    handleView(contextMenu.file);
                    setContextMenu(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              )}
              {hasPermission(contextMenu.file, 'download') && (
                <button
                  onClick={() => {
                    handleDownload(contextMenu.file);
                    setContextMenu(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              )}
              {/* Show Access Logs and Statistics only if user is owner */}
              {dataroom?.is_owner && (
                <>
                  <button
                    onClick={() => {
                      setSelectedFileForLogs(contextMenu.file);
                      setShowAccessLogs(true);
                      setContextMenu(null);
                    }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Activity className="w-4 h-4" />
                    Access Logs
                  </button>
                  <button
                    onClick={() => {
                      setSelectedFileForLogs(contextMenu.file);
                      setShowStatistics(true);
                      setContextMenu(null);
                    }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Statistics
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setSelectedFileForTag(contextMenu.file);
                  fetchAvailableTags();
                  setShowTagModal(true);
                  setContextMenu(null);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
              >
                <Tag className="w-4 h-4" />
                Assign Tags
              </button>
              <div className="border-t border-gray-200 my-1"></div>
              <button
                onClick={() => {
                  handleMarkAsRead(contextMenu.file);
                  setContextMenu(null);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 group"
              >
                <BookOpen className="w-4 h-4 text-green-600" />
                <span className="flex-1">Mark as Read</span>
                {contextMenu.file.is_read === true && (
                  <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">Current</span>
                )}
              </button>
              <button
                onClick={() => {
                  handleMarkAsUnread(contextMenu.file);
                  setContextMenu(null);
                }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 group"
              >
                <BookX className="w-4 h-4 text-gray-500" />
                <span className="flex-1">Mark as Unread</span>
                {contextMenu.file.is_read === false && (
                  <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">Current</span>
                )}
              </button>
              <div className="border-t border-gray-200 my-1"></div>
              {/* Favorite options */}
              {contextMenu.file.is_favorite ? (
                <button
                  onClick={() => {
                    handleRemoveFromFavorite(contextMenu.file);
                    setContextMenu(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <StarOff className="w-4 h-4 text-gray-500" />
                  <span>Remove from Favorites</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleAddToFavorite(contextMenu.file);
                    setContextMenu(null);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span>Add to Favorites</span>
                </button>
              )}
            </>
          )}
          {hasPermission(contextMenu.file, 'copy') && (
            <button
              onClick={() => {
                handleCopy(contextMenu.file);
                setContextMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy
            </button>
          )}
          {hasPermission(contextMenu.file, 'move') && (
            <button
              onClick={() => {
                handleMove(contextMenu.file);
                setContextMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <Move className="w-4 h-4" />
              Move
            </button>
          )}
          {hasPermission(contextMenu.file, 'rename') && (
            <button
              onClick={() => {
                setFileToRename(contextMenu.file);
                setNewFileName(contextMenu.file.name);
                setShowRenameModal(true);
                setContextMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Rename
            </button>
          )}
          {hasPermission(contextMenu.file, 'delete') && (
            <button
              onClick={() => {
                confirmDelete(contextMenu.file);
                setContextMenu(null);
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
            </>
          )}
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative"
          >
            <button
              onClick={() => {
                setShowNewFolderModal(false);
                setNewFolderName('');
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Folder</h3>
            <input
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createFolder()}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <CancelButton
                onClick={() => {
                  setShowNewFolderModal(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </CancelButton>
              <CreateButton
                onClick={createFolder}
              >
                Create
              </CreateButton>
            </div>
          </motion.div>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && fileToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative"
          >
            <button
              onClick={() => {
                setShowRenameModal(false);
                setFileToRename(null);
                setNewFileName('');
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rename</h3>
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleRename()}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <CancelButton
                onClick={() => {
                  setShowRenameModal(false);
                  setFileToRename(null);
                  setNewFileName('');
                }}
              >
                Cancel
              </CancelButton>
              <UpdateButton
                onClick={handleRename}
              >
                Rename
              </UpdateButton>
            </div>
          </motion.div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-preview-modal="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col relative no-select secure-preview"
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <button
              onClick={() => {
                // Clean up when closing preview
                if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(pdfBlobUrl);
                }
                if (watermarkedPdfUrl && watermarkedPdfUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(watermarkedPdfUrl);
                }
                setPdfBlobUrl(null);
                setWatermarkedPdfUrl(null);
                setPreviewFile(null);
                setLoadingPdf(false);
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header - Fixed height */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900 pr-12">{previewFile.display_name || previewFile.name}</h3>
            </div>

            {/* Content - Flexible height */}
            <div className="flex-1 min-h-0 overflow-auto">
              {/* Preview content - only PDFs and images reach here */}
              {(() => {

                if (previewFile.mimeType && previewFile.mimeType.startsWith('image/')) {
                  // Image preview fitted to modal dimensions
                  const imageContent = (
                    <div
                      className="w-full flex items-center justify-center bg-gray-100 rounded-lg p-4"
                      style={{ minHeight: '65vh' }}
                    >
                      <img
                        src={previewFile.url}
                        alt={previewFile.name || previewFile.display_name}
                        className="rounded shadow-lg"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', previewFile.url);
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"%3E%3C/rect%3E%3Ctext x="100" y="100" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="16"%3EImage Load Error%3C/text%3E%3C/svg%3E';
                        }}
                        onLoad={(e) => {
                          console.log('Image loaded successfully:', previewFile.url);
                        }}
                      />
                    </div>
                  );

                  // Image is already watermarked by watermarkService, no need to wrap with Watermark component
                  return imageContent;
                } else if (previewFile.needsPDFViewer) {
                  // Check if URL is valid before rendering
                  if (!previewFile.url) {
                    return (
                      <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 className="w-16 h-16 text-gray-300 mb-4 animate-spin" />
                        <p className="text-gray-500">Loading preview...</p>
                      </div>
                    );
                  }

                  // Use WatermarkedPDFPreview component (watermark already applied by backend, so watermarkEnabled=false)
                  return (
                    <div className="h-full flex flex-col" style={{ minHeight: '70vh' }}>
                      <WatermarkedPDFPreview
                        pdfUrl={previewFile.url}
                        watermarkInfo={null}
                        watermarkEnabled={false}
                        watermarkFontSize={watermarkFontSize}
                        fileName={previewFile.fileName || previewFile.display_name || previewFile.name}
                        hideDownloadButton={true}
                        onClose={() => {
                          // Close the preview modal
                          if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(pdfBlobUrl);
                          }
                          if (watermarkedPdfUrl && watermarkedPdfUrl.startsWith('blob:')) {
                            URL.revokeObjectURL(watermarkedPdfUrl);
                          }
                          setPdfBlobUrl(null);
                          setWatermarkedPdfUrl(null);
                          setPreviewFile(null);
                          setLoadingPdf(false);
                        }}
                        onDownload={(url) => {
                          setWatermarkedPdfUrl(url);
                        }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="flex flex-col items-center justify-center h-64">
                      <File className="w-16 h-16 text-gray-300 mb-4" />
                      <p className="text-gray-500">Preview not available for this file type</p>
                      <p className="text-xs text-gray-400 mt-2">{previewFile.mimeType || 'Unknown type'}</p>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Download button at the bottom of modal for images and PDFs - Fixed height */}
            {previewFile && (previewFile.needsPDFViewer || previewFile.mimeType === 'application/pdf' || previewFile.mimeType?.startsWith('image/')) && (
              <div className="flex justify-end gap-3 mt-4 border-t pt-4 flex-shrink-0">
                <button
                  onClick={() => {
                    // Use the same preview URL for download
                    const downloadUrl = previewFile.url;
                    const fileName = previewFile.fileName || previewFile.display_name || previewFile.name || 'file';
                    if (downloadUrl) {
                      // Create download link and trigger download
                      const link = document.createElement('a');
                      link.href = downloadUrl;
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success(`Download started: ${fileName}`);
                    } else {
                      toast.error('Download URL not available');
                    }
                  }}
                  disabled={!previewFile.url}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download file"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Copy Modal */}
      {showCopyModal && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[70vh] overflow-hidden relative"
          >
            <button
              onClick={() => {
                setShowCopyModal(false);
                setSelectedFile(null);
                setDestinationFolder(null);
                setCopyName('');
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Copy {selectedFile.type === 'folder' ? 'Folder' : 'File'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New name (optional)
              </label>
              <input
                type="text"
                value={copyName}
                onChange={(e) => setCopyName(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder={selectedFile.display_name || selectedFile.name}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select destination folder
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-64 overflow-y-auto">
                {/* Root level */}
                <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="radio"
                    name="destination"
                    value=""
                    checked={destinationFolder === null}
                    onChange={() => setDestinationFolder(null)}
                    className="text-primary-600"
                  />
                  <Home className="w-4 h-4 text-gray-600" />
                  <span className="text-sm">Home</span>
                </label>
                
                {/* Render folder tree */}
                {folderTree.map((folder) => (
                  <label
                    key={folder.uuid || folder.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    style={{ paddingLeft: `${((folder.level || 0) + 1) * 1.5}rem` }}
                  >
                    <input
                      type="radio"
                      name="destination"
                      value={folder.uuid || folder.id}
                      checked={destinationFolder === (folder.uuid || folder.id)}
                      onChange={() => setDestinationFolder(folder.uuid || folder.id)}
                      disabled={(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) || 
                               (folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id)}
                      className="text-primary-600"
                    />
                    <Folder className="w-4 h-4 text-blue-500" />
                    <span className={`text-sm ${(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) || (folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id) ? 'text-gray-400' : ''}`}>
                      {folder.display_name || folder.name}
                      {(folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id) && ' (current location)'}
                      {(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) && ' (self)'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCopyModal(false);
                  setSelectedFile(null);
                  setDestinationFolder(null);
                  setCopyName('');
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={executeCopy}
                disabled={destinationFolder === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id)}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[70vh] overflow-hidden relative"
          >
            <button
              onClick={() => {
                setShowMoveModal(false);
                setSelectedFile(null);
                setDestinationFolder(null);
              }}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Move {selectedFile.type === 'folder' ? 'Folder' : 'File'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Moving: <span className="font-medium">{selectedFile.display_name || selectedFile.name}</span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select destination folder
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-64 overflow-y-auto">
                {/* Root level */}
                <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="radio"
                    name="moveDestination"
                    value=""
                    checked={destinationFolder === null}
                    onChange={() => setDestinationFolder(null)}
                    className="text-primary-600"
                  />
                  <Home className="w-4 h-4 text-gray-600" />
                  <span className="text-sm">Home</span>
                </label>
                
                {/* Render folder tree */}
                {folderTree.map((folder) => (
                  <label
                    key={folder.uuid || folder.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    style={{ paddingLeft: `${((folder.level || 0) + 1) * 1.5}rem` }}
                  >
                    <input
                      type="radio"
                      name="moveDestination"
                      value={folder.uuid || folder.id}
                      checked={destinationFolder === (folder.uuid || folder.id)}
                      onChange={() => setDestinationFolder(folder.uuid || folder.id)}
                      disabled={(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) || 
                               (folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id)}
                      className="text-primary-600"
                    />
                    <Folder className="w-4 h-4 text-blue-500" />
                    <span className={`text-sm ${(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) || (folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id) ? 'text-gray-400' : ''}`}>
                      {folder.display_name || folder.name}
                      {(folder.uuid || folder.id) === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id) && ' (current location)'}
                      {(folder.uuid || folder.id) === (selectedFile?.uuid || selectedFile?.id) && ' (self)'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setSelectedFile(null);
                  setDestinationFolder(null);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={executeMove}
                disabled={destinationFolder === (selectedFile?.parent_folder_uuid || selectedFile?.parent_id)}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Move className="w-4 h-4" />
                Move
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* File Access Logs Modal */}
      <FileAccessLogs
        isOpen={showAccessLogs}
        onClose={() => {
          setShowAccessLogs(false);
          setSelectedFileForLogs(null);
        }}
        file={selectedFileForLogs}
        dataroomUuid={dataroomUuid}
      />

      {/* File Statistics Modal */}
      <FileStatistics
        isOpen={showStatistics}
        onClose={() => {
          setShowStatistics(false);
          setSelectedFileForLogs(null);
        }}
        file={selectedFileForLogs}
        dataroomUuid={dataroomUuid}
      />

      {/* Watermark Settings Modal */}
      <WatermarkSettings
        isOpen={showWatermarkSettings}
        onClose={() => setShowWatermarkSettings(false)}
        dataroomUuid={dataroomUuid}
        currentStatus={watermarkEnabled}
        onUpdate={(newStatus) => {
          setWatermarkEnabled(newStatus);
        }}
      />

      {/* Tag Assignment Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Assign Tags to File
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedFileForTag?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTagModal(false);
                  setSelectedFileForTag(null);
                  setSelectedTagIds([]);
                }}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {loadingTags ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
              </div>
            ) : availableTags.length === 0 ? (
              <div className="text-center py-8">
                <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No tags available</p>
                <p className="text-sm text-gray-400 mt-1">
                  Please create tags first from the Tags section
                </p>
                <button
                  onClick={() => fetchAvailableTags()}
                  className="mt-4 px-3 py-1.5 text-sm bg-primary-100 hover:bg-primary-200 text-primary-700 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh Tags
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Tags to Assign
                  </label>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    <div className="space-y-2">
                      {availableTags.map(tag => {
                        // Check if tag is already assigned to the file
                        const isAlreadyAssigned = selectedFileForTag?.tags?.some(
                          existingTag => existingTag.id === tag.id
                        );
                        
                        return (
                          <label
                            key={tag.id}
                            className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                              isAlreadyAssigned 
                                ? 'bg-gray-50 opacity-50 cursor-not-allowed' 
                                : selectedTagIds.includes(tag.id)
                                  ? 'bg-primary-50 border border-primary-300'
                                  : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTagIds.includes(tag.id)}
                              onChange={() => !isAlreadyAssigned && toggleTagSelection(tag.id)}
                              disabled={isAlreadyAssigned}
                              className="mr-3 h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4 text-gray-500" />
                                <span className={`text-sm font-medium ${
                                  isAlreadyAssigned ? 'text-gray-400' : 'text-gray-900'
                                }`}>
                                  {tag.name}
                                </span>
                                {isAlreadyAssigned && (
                                  <span className="text-xs text-gray-500 ml-2">(Already assigned)</span>
                                )}
                              </div>
                              {tag.description && (
                                <p className="text-xs text-gray-500 mt-1 ml-7">{tag.description}</p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {selectedTagIds.length > 0 && (
                    <div className="mt-2 text-sm text-primary-600 font-medium">
                      {selectedTagIds.length} tag(s) selected for assignment
                    </div>
                  )}
                </div>

                {/* Currently assigned tags */}
                {selectedFileForTag?.tags && selectedFileForTag.tags.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Current Tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedFileForTag.tags.map(tag => (
                        <span
                          key={tag.id}
                          className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium flex items-center gap-1"
                        >
                          {tag.name}
                          <button
                            onClick={async () => {
                              try {
                                await fileService.removeTagFromFile(
                                  dataroomUuid,
                                  selectedFileForTag.uuid || selectedFileForTag.id,
                                  tag.id
                                );
                                toast.success('Tag removed successfully');
                                // Update local state
                                setSelectedFileForTag({
                                  ...selectedFileForTag,
                                  tags: selectedFileForTag.tags.filter(t => t.id !== tag.id)
                                });
                                fetchFiles(currentFolder);
                              } catch (error) {
                                toast.error('Failed to remove tag');
                              }
                            }}
                            className="hover:bg-primary-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowTagModal(false);
                      setSelectedFileForTag(null);
                      setSelectedTagIds([]);
                    }}
                    className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignTags}
                    disabled={selectedTagIds.length === 0 || assigningTags}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {assigningTags ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Assigning...
                      </>
                    ) : (
                      `Assign ${selectedTagIds.length > 1 ? `${selectedTagIds.length} Tags` : 'Tag'}`
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setFileToDelete(null);
        }}
        title="Delete File"
        size="small"
      >
        <p className="text-sm text-gray-500 mb-4">
          Are you sure you want to delete <strong>{fileToDelete?.display_name || fileToDelete?.name}</strong>?
          {fileToDelete?.type === 'folder' && (
            <span className="block mt-2 text-red-600">
              Warning: This will also delete all files and subfolders inside this folder.
            </span>
          )}
          <span className="block mt-2">This action cannot be undone.</span>
        </p>
        <div className="flex justify-end gap-3">
          <CancelButton
            onClick={() => {
              setShowDeleteModal(false);
              setFileToDelete(null);
            }}
          />
          <DeleteButton
            onClick={handleDelete}
            disabled={isDeleting}
            className={`${
              isDeleting 
                ? 'bg-red-400 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700'
            } text-white px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-2`}
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Deleting...
              </>
            ) : (
              `Delete ${fileToDelete?.type === 'folder' ? 'Folder' : 'File'}`
            )}
          </DeleteButton>
        </div>
      </ResponsiveModal>

      {/* Bulk Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showBulkDeleteModal}
        onClose={() => {
          setShowBulkDeleteModal(false);
        }}
        title="Delete Multiple Files"
        size="small"
      >
        <p className="text-sm text-gray-500 mb-4">
          Are you sure you want to delete <strong>{selectedFiles.length} file(s)</strong>?
          <span className="block mt-2 text-red-600">
            This action cannot be undone.
          </span>
        </p>
        <div className="flex justify-end gap-3">
          <CancelButton
            onClick={() => {
              setShowBulkDeleteModal(false);
            }}
          />
          <DeleteButton
            onClick={handleDeleteMultiple}
            disabled={isDeleting}
            className={`${
              isDeleting
                ? 'bg-red-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            } text-white px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-2`}
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Deleting...
              </>
            ) : (
              `Delete ${selectedFiles.length} File(s)`
            )}
          </DeleteButton>
        </div>
      </ResponsiveModal>
            </div>
          </div>
        </div>
      )}

      {/* Tags Tab Content */}
      {activeTab === 'tags' && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6">
              <DataroomTags dataroomUuid={dataroomUuid} isUserContext={true} />
            </div>
          </div>
        </div>
      )}

      {/* Invitations Tab Content - Only show if user is owner */}
      {activeTab === 'invitations' && dataroom?.is_owner !== false && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6">
              <Invitations />
            </div>
          </div>
        </div>
      )}

      {/* Roles Tab Content - Only show if user is owner */}
      {activeTab === 'roles' && dataroom?.is_owner !== false && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6">
              <DataroomRoles dataroomUuid={dataroomUuid} isUserContext={true} />
            </div>
          </div>
        </div>
      )}

      {/* Members Tab Content - Only show if user is owner */}
      {activeTab === 'members' && dataroom?.is_owner !== false && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6">
              <DataroomMembers dataroomUuid={dataroomUuid} isUserContext={true} />
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab Content */}
      {(activeTab === 'notifications' || activeTab === 'email-notifications') && (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Icon Sidebar */}
          <DataroomSidebar
            dataroomUuid={dataroomUuid}
            isOwner={dataroom?.is_owner}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            <div className="p-6 space-y-4">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-4 px-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Email Notification Settings</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Configure how and when you receive email notifications for this dataroom
                  </p>
                </div>
                <CreateButton
                  onClick={() => setShowEmailPrefs(true)}
                  icon={Bell}
                >
                  Configure Preferences
                </CreateButton>
              </div>
            </div>

            {/* Content Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-6 px-6">
              <div className="max-w-2xl">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <Bell className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Manage Your Email Notifications
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Control when and how often you receive email notifications about activity in this dataroom.
                        You can choose to use global settings or customize notifications specifically for this dataroom.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>Set notification frequency (hourly, daily, weekly, etc.)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>Choose your preferred time to receive daily summaries</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>Enable or disable notifications for this dataroom</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>Use global settings or customize per dataroom</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Notification Preferences Modal */}
      {showEmailPrefs && (
        <EmailNotificationPrefs
          isOpen={showEmailPrefs}
          onClose={() => setShowEmailPrefs(false)}
          dataroomUuid={dataroomUuid}
          dataroomName={dataroom?.name}
        />
      )}

      {/* Import Template Modal */}
      <ImportTemplateModal
        isOpen={showImportTemplateModal}
        onClose={() => setShowImportTemplateModal(false)}
        onImport={handleImportTemplate}
        dataroomId={dataroomUuid}
        currentFolderName={breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : null}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        dataroomUuid={dataroomUuid}
        currentFolder={currentFolder}
        onUploadComplete={async () => {
          console.log('🔄 Upload completed, refreshing file list for folder:', currentFolder);

          // Force refresh by clearing files first
          setFiles([]);

          // Wait a moment for the state to clear
          await new Promise(resolve => setTimeout(resolve, 100));

          // Then fetch fresh data with forceRefresh flag
          await fetchFiles(currentFolder, null, true);

          // Close modal after refresh completes
          setShowUploadModal(false);
        }}
      />

      {/* Upload Tracker */}
      <UploadTracker
        isOpen={showUploadTracker}
        onClose={() => setShowUploadTracker(false)}
      />

      {/* Edit Dataroom Modal */}
      {showEditDataroomModal && (
        <ResponsiveModal
          isOpen={showEditDataroomModal}
          onClose={() => setShowEditDataroomModal(false)}
          title="Edit Dataroom"
          size="large"
        >
          <EditDataroomForm
            dataroom={dataroom}
            onSave={handleUpdateDataroom}
            onCancel={() => setShowEditDataroomModal(false)}
          />
        </ResponsiveModal>
      )}

    </div>
  );
}
export default FileManager;